import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../../firebase-client';
import SubmitButton from '../../formComponents/SubmitButton';
import ErrorMessage from '../../formComponents/ErrorMessage';
import useStripePaymentGuard from '../../../payments/useStripePaymentGuard';
import {
  buildMooringPublicMapView,
  canvasFromMooringArea,
  deriveMooringPaymentStatus,
  displayNameFromProfile,
  gridDimensionsFromCanvas,
  isMooringWaitlistOpen,
  metresFromMooringVisual,
  minimumCanvasForMooringSpaces,
  MOORING_CANVAS,
  MOORING_CANVAS_MIN,
  mooringWaitlistSpaceMatches,
  mooringWaitlistTargetLabel,
  mooringVisualFromMetres,
  mooringPaymentCadences,
  normalizeMooringVisual,
  resizeMooringCanvas,
  resolveMooringPaymentPrice,
  snapMooringVisual,
  sortMooringWaitlist,
  summarizeMoorings,
  suggestMooringSpaces,
  waitlistQueueForSpace,
  visualFromMooringDrag,
} from '../../../teams/utils/moorings';
import { getCurrencyMeta } from '../../../../utils/currency';
import { safeSailingBoatLabel } from '../../../teams/utils/sailingFleet';
import {
  copyableMooringSpaceFields,
  directionalCopyLayout,
  marqueeRect,
  numberedCopyDefaults,
  rectsIntersect,
  selectedSpaceDeleteBlockers,
  uniqueMooringMapLabel,
} from './mooringAdminUtils';
import '../../../../styles/moorings.css';

const AREA_TYPES = [
  ['pontoon', 'Pontoon'],
  ['mooring_field', 'Mooring field'],
  ['storage_bay', 'Storage bay'],
  ['dinghy_park', 'Dinghy park'],
];

const SPACE_STATUSES = [
  ['vacant', 'Vacant'],
  ['occupied', 'Occupied'],
  ['reserved', 'Reserved'],
  ['maintenance', 'Maintenance blocked'],
];

const VISIBILITY_OPTIONS = [
  ['admin_only', 'Admin only'],
  ['my_allocation', 'Members can see their own allocation'],
  ['availability', 'Members can see availability'],
];

const MAP_TOOLS = [
  ['select', 'Move'],
  ['vacant', 'Vacant'],
  ['reserved', 'Reserved'],
  ['maintenance', 'Block'],
  ['label', 'Label'],
];

const AREA_TEMPLATES = [
  ['blank', 'Blank canvas'],
  ['single_pontoon', 'Single pontoon'],
  ['double_pontoon', 'Double pontoon'],
  ['field_grid', 'Mooring field'],
  ['storage_grid', 'Storage grid'],
];

function cleanText(value) {
  return String(value || '').trim();
}

function newBoatClassId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `boat-class-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function numberOrBlank(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? String(number) : '';
}

function moneyFromMinor(value) {
  const number = Number(value);
  return Number.isFinite(number) && number >= 0 ? (number / 100).toFixed(2) : '';
}

function moneyToMinor(value) {
  const text = cleanText(value);
  if (!text) return 0;
  const number = Number(text);
  return Number.isFinite(number) && number >= 0 ? Math.round(number * 100) : 0;
}

function normalizePayerChoicePriceEntryMode(value) {
  const mode = String(value || '').trim();
  return ['separate', 'annual', 'monthly'].includes(mode) ? mode : 'annual';
}

function linkedMonthlyAnnualAmounts(monthlyValue, annualValue, payment = {}) {
  const monthly = moneyToMinor(monthlyValue);
  const annual = moneyToMinor(annualValue);
  if (payment?.cadenceMode !== 'payer_choice') return { monthly, annual };
  const mode = normalizePayerChoicePriceEntryMode(payment?.payerChoicePriceEntryMode);
  if (mode === 'annual') return { monthly: annual > 0 ? Math.round(annual / 12) : 0, annual };
  if (mode === 'monthly') return { monthly, annual: monthly > 0 ? monthly * 12 : 0 };
  return { monthly, annual };
}

function createPriceDraft(prices = {}) {
  return {
    memberMonthlyAmount: moneyFromMinor(prices?.member?.monthlyAmount),
    memberAnnualAmount: moneyFromMinor(prices?.member?.annualAmount),
    nonMemberMonthlyAmount: moneyFromMinor(prices?.non_member?.monthlyAmount),
    nonMemberAnnualAmount: moneyFromMinor(prices?.non_member?.annualAmount),
  };
}

function pricePayloadFromDraft(draft = {}, payment = {}) {
  const member = linkedMonthlyAnnualAmounts(draft.memberMonthlyAmount, draft.memberAnnualAmount, payment);
  const nonMember = linkedMonthlyAnnualAmounts(draft.nonMemberMonthlyAmount, draft.nonMemberAnnualAmount, payment);
  return {
    member: {
      monthlyAmount: member.monthly,
      annualAmount: member.annual,
    },
    non_member: {
      monthlyAmount: nonMember.monthly,
      annualAmount: nonMember.annual,
    },
  };
}

function createRateDraft(rates = {}) {
  return {
    memberMonthlyAmountPerMetre: moneyFromMinor(rates?.member?.monthlyAmountPerMetre),
    memberAnnualAmountPerMetre: moneyFromMinor(rates?.member?.annualAmountPerMetre),
    nonMemberMonthlyAmountPerMetre: moneyFromMinor(rates?.non_member?.monthlyAmountPerMetre),
    nonMemberAnnualAmountPerMetre: moneyFromMinor(rates?.non_member?.annualAmountPerMetre),
  };
}

function ratePayloadFromDraft(draft = {}, payment = {}) {
  const member = linkedMonthlyAnnualAmounts(draft.memberMonthlyAmountPerMetre, draft.memberAnnualAmountPerMetre, payment);
  const nonMember = linkedMonthlyAnnualAmounts(draft.nonMemberMonthlyAmountPerMetre, draft.nonMemberAnnualAmountPerMetre, payment);
  return {
    member: {
      monthlyAmountPerMetre: member.monthly,
      annualAmountPerMetre: member.annual,
    },
    non_member: {
      monthlyAmountPerMetre: nonMember.monthly,
      annualAmountPerMetre: nonMember.annual,
    },
  };
}

function createSquareRateDraft(rates = {}) {
  return {
    memberMonthlyAmountPerSquareMetre: moneyFromMinor(rates?.member?.monthlyAmountPerSquareMetre),
    memberAnnualAmountPerSquareMetre: moneyFromMinor(rates?.member?.annualAmountPerSquareMetre),
    nonMemberMonthlyAmountPerSquareMetre: moneyFromMinor(rates?.non_member?.monthlyAmountPerSquareMetre),
    nonMemberAnnualAmountPerSquareMetre: moneyFromMinor(rates?.non_member?.annualAmountPerSquareMetre),
  };
}

function squareRatePayloadFromDraft(draft = {}, payment = {}) {
  const member = linkedMonthlyAnnualAmounts(draft.memberMonthlyAmountPerSquareMetre, draft.memberAnnualAmountPerSquareMetre, payment);
  const nonMember = linkedMonthlyAnnualAmounts(draft.nonMemberMonthlyAmountPerSquareMetre, draft.nonMemberAnnualAmountPerSquareMetre, payment);
  return {
    member: {
      monthlyAmountPerSquareMetre: member.monthly,
      annualAmountPerSquareMetre: member.annual,
    },
    non_member: {
      monthlyAmountPerSquareMetre: nonMember.monthly,
      annualAmountPerSquareMetre: nonMember.annual,
    },
  };
}

function createPaymentDraft(payment = {}, areas = []) {
  const areaDrafts = {};
  const areaRateDrafts = {};
  areas.forEach((area) => {
    areaDrafts[area.id] = createPriceDraft(payment?.areaPrices?.[area.id] || area.mooringPrice || {});
    areaRateDrafts[area.id] = createSquareRateDraft(payment?.areaRates?.[area.id] || area.mooringRate || {});
  });
  return {
    enabled: true,
    baselineCalculation: payment?.baselineCalculation || 'flat',
    cadenceMode: payment?.cadenceMode || 'annual',
    payerChoicePriceEntryMode: normalizePayerChoicePriceEntryMode(payment?.payerChoicePriceEntryMode),
    stripeFeeHandling: payment?.stripeFeeHandling || 'deduct',
    areaPricingMode: payment?.areaPricingMode || 'fixed',
    flatPrices: createPriceDraft(payment?.flatPrices),
    lengthRates: createRateDraft(payment?.lengthRates),
    areaPrices: areaDrafts,
    areaRates: areaRateDrafts,
    boatClasses: Array.isArray(payment?.boatClasses)
      ? payment.boatClasses.map((boatClass) => ({
        id: cleanText(boatClass?.id) || newBoatClassId(),
        name: boatClass?.name || '',
        prices: createPriceDraft(boatClass?.prices),
      }))
      : [],
  };
}

function paymentPayloadFromDraft(draft = {}) {
  const areaPrices = {};
  Object.entries(draft.areaPrices || {}).forEach(([areaId, priceDraft]) => {
    areaPrices[areaId] = pricePayloadFromDraft(priceDraft, draft);
  });
  const areaRates = {};
  Object.entries(draft.areaRates || {}).forEach(([areaId, rateDraft]) => {
    areaRates[areaId] = squareRatePayloadFromDraft(rateDraft, draft);
  });
  const boatClasses = (draft.boatClasses || []).map((boatClass) => ({
    id: cleanText(boatClass.id),
    name: cleanText(boatClass.name),
    prices: pricePayloadFromDraft(boatClass.prices, draft),
  }));
  return {
    enabled: true,
    baselineCalculation: ['flat', 'area', 'length', 'space', 'class'].includes(draft.baselineCalculation) ? draft.baselineCalculation : 'flat',
    cadenceMode: ['monthly', 'annual', 'payer_choice'].includes(draft.cadenceMode) ? draft.cadenceMode : 'annual',
    payerChoicePriceEntryMode: normalizePayerChoicePriceEntryMode(draft.payerChoicePriceEntryMode),
    stripeFeeHandling: draft.stripeFeeHandling === 'add' ? 'add' : 'deduct',
    areaPricingMode: draft.areaPricingMode === 'sqm' ? 'sqm' : 'fixed',
    flatPrices: pricePayloadFromDraft(draft.flatPrices, draft),
    lengthRates: ratePayloadFromDraft(draft.lengthRates, draft),
    areaPrices,
    areaRates,
    boatClasses,
    boatClassIds: boatClasses.map((boatClass) => boatClass.id),
  };
}

function parseOptionalNumber(value) {
  const text = cleanText(value);
  if (!text) return null;
  const number = Number(text);
  return Number.isFinite(number) && number >= 0 ? number : null;
}

function localDateValue(value) {
  if (!value) return '';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function formatShortDate(value) {
  const date = value?.toDate ? value.toDate() : (value ? new Date(value) : null);
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function memberNameById(memberProfiles = []) {
  return new Map(memberProfiles.map((member) => [member.id, displayNameFromProfile(member)]));
}

function memberEmailById(memberProfiles = []) {
  return new Map(memberProfiles.map((member) => [member.id, cleanText(member.email || member.emailAddress || member.contactEmail)]));
}

function createAreaDraft(area = {}) {
  const canvas = canvasFromMooringArea(area);
  return {
    id: area.id || '',
    name: area.name || '',
    type: area.type || 'pontoon',
    rows: String(Math.max(1, Number(area.rows || 4))),
    columns: String(Math.max(1, Number(area.columns || 8))),
    canvasWidth: String(Math.round(canvas.width)),
    canvasHeight: String(Math.round(canvas.height)),
    sortOrder: String(Number.isFinite(Number(area.sortOrder)) ? Number(area.sortOrder) : 0),
    template: area.template || 'blank',
  };
}

function createSpaceDraft(space = {}, canvas = MOORING_CANVAS) {
  const visual = normalizeMooringVisual(space, canvas);
  return {
    id: space.id || '',
    areaId: space.areaId || '',
    label: space.label || '',
    row: String(Math.max(1, Number(space.row || 1))),
    column: String(Math.max(1, Number(space.column || 1))),
    status: space.status || 'vacant',
    maxLength: numberOrBlank(space.maxLength),
    maxBeam: numberOrBlank(space.maxBeam),
    maxDraft: numberOrBlank(space.maxDraft),
    vesselTypes: Array.isArray(space.vesselTypes) ? space.vesselTypes.join(', ') : '',
    clubBoatId: space.clubBoatId || '',
    clubBoatLabel: space.clubBoatLabel || '',
    boltOnId: space.boltOnId || '',
    mooringPrice: createPriceDraft(space.mooringPrice),
    notes: space.notes || '',
    x: String(visual.x),
    y: String(visual.y),
    width: String(visual.width),
    height: String(visual.height),
    shape: visual.shape,
    rotation: String(visual.rotation),
    labelPosition: visual.labelPosition,
  };
}

function createAllocationDraft(allocation = {}) {
  return {
    id: allocation.id || '',
    spaceId: allocation.spaceId || '',
    memberId: allocation.memberId || '',
    memberNameSnapshot: allocation.memberNameSnapshot || '',
    payerType: allocation.payerType === 'non_member' ? 'non_member' : 'member',
    payerEmail: allocation.payerEmail || '',
    paymentLinkId: allocation.paymentLinkId || '',
    customPrices: createPriceDraft(allocation.customPrices || allocation.mooringPrice),
    status: allocation.status || 'active',
    vesselName: allocation.vesselName || '',
    vesselType: allocation.vesselType || '',
    boatClassId: allocation.boatClassId || '',
    boatClassNameSnapshot: allocation.boatClassNameSnapshot || '',
    length: numberOrBlank(allocation.length),
    beam: numberOrBlank(allocation.beam),
    draft: numberOrBlank(allocation.draft),
    startDate: localDateValue(allocation.startDate),
    endDate: localDateValue(allocation.endDate),
    boltOnId: allocation.boltOnId || '',
    paymentOverrideStatus: allocation.paymentOverrideStatus || '',
    privateNotes: allocation.privateNotes || '',
  };
}

function createWaitlistDraft(entry = {}) {
  return {
    id: entry.id || '',
    memberId: entry.memberId || '',
    status: entry.status || 'waiting',
    requestType: entry.requestType || (entry.spaceId || entry.spaceLabel ? 'space' : 'general'),
    spaceId: entry.spaceId || '',
    spaceLabel: entry.spaceLabel || '',
    areaName: entry.areaName || '',
    offeredSpaceId: entry.offeredSpaceId || '',
    offeredSpaceLabel: entry.offeredSpaceLabel || '',
    offeredAreaName: entry.offeredAreaName || '',
    allocationId: entry.allocationId || '',
    allocatedSpaceId: entry.allocatedSpaceId || '',
    priority: String(Number.isFinite(Number(entry.priority)) ? Number(entry.priority) : ''),
    appliedAt: localDateValue(entry.appliedAt) || localDateValue(new Date()),
    vesselName: entry.vesselName || '',
    vesselType: entry.vesselType || '',
    proposedBoatClassId: entry.proposedBoatClassId || '',
    proposedBoatClassNameSnapshot: entry.proposedBoatClassNameSnapshot || '',
    boatClassId: entry.boatClassId || entry.proposedBoatClassId || '',
    boatClassNameSnapshot: entry.boatClassNameSnapshot || entry.proposedBoatClassNameSnapshot || '',
    length: numberOrBlank(entry.length),
    beam: numberOrBlank(entry.beam),
    draft: numberOrBlank(entry.draft),
    memberNote: entry.memberNote || '',
    adminNotes: entry.adminNotes || '',
    decisionNote: entry.decisionNote || '',
  };
}

function createMaintenanceDraft(entry = {}) {
  return {
    id: entry.id || '',
    spaceId: entry.spaceId || '',
    type: entry.type || 'inspection',
    status: entry.status || 'open',
    title: entry.title || '',
    dueDate: localDateValue(entry.dueDate),
    completedAt: localDateValue(entry.completedAt),
    notes: entry.notes || '',
  };
}

function visualFromDraft(draft = {}, canvas = MOORING_CANVAS) {
  return snapMooringVisual({
    x: Number(draft.x),
    y: Number(draft.y),
    width: Number(draft.width),
    height: Number(draft.height),
    shape: draft.shape || 'rect',
    rotation: Number(draft.rotation || 0),
    labelPosition: draft.labelPosition || 'inside',
  }, canvas.snap || MOORING_CANVAS.snap, canvas);
}

function draftWithVisual(draft = {}, visual = {}) {
  const position = rowColumnFromVisual(visual);
  return {
    ...draft,
    row: String(position.row),
    column: String(position.column),
    x: String(visual.x),
    y: String(visual.y),
    width: String(visual.width),
    height: String(visual.height),
    shape: visual.shape,
    rotation: String(visual.rotation),
    labelPosition: visual.labelPosition,
  };
}

function rowColumnFromVisual(visual = {}) {
  return {
    row: Math.max(1, Math.round(Number(visual.y || 0) / (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap)) + 1),
    column: Math.max(1, Math.round(Number(visual.x || 0) / (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap)) + 1),
  };
}

function createTemplateSpaces(area = {}, template = 'blank') {
  if (template === 'blank') return [];
  const rows = Math.max(1, Number(area.rows || 4));
  const columns = Math.max(1, Number(area.columns || 8));
  const canvas = canvasFromMooringArea(area);
  const spaces = [];
  const pushSpace = ({ row, column, x, y, width = MOORING_CANVAS.spotWidth, height = MOORING_CANVAS.spotHeight, shape = 'rect' }) => {
    const visual = snapMooringVisual({ x, y, width, height, shape, rotation: 0, labelPosition: 'inside' }, canvas.snap, canvas);
    spaces.push({
      areaId: area.id,
      label: `${area.name || 'M'}-${row}-${column}`,
      row,
      column,
      status: 'vacant',
      ...visual,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  };

  if (template === 'single_pontoon') {
    for (let column = 1; column <= columns; column += 1) {
      pushSpace({ row: 1, column, x: MOORING_CANVAS.snap + (column - 1) * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap), y: MOORING_CANVAS.snap });
    }
    return spaces;
  }

  if (template === 'double_pontoon') {
    for (let column = 1; column <= columns; column += 1) {
      pushSpace({ row: 1, column, x: MOORING_CANVAS.snap + (column - 1) * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap), y: MOORING_CANVAS.snap });
      pushSpace({ row: 2, column, x: MOORING_CANVAS.snap + (column - 1) * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap), y: MOORING_CANVAS.snap + (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap) });
    }
    return spaces;
  }

  const shape = template === 'field_grid' ? 'circle' : 'rect';
  const width = template === 'field_grid' ? 56 : MOORING_CANVAS.spotWidth;
  const height = template === 'field_grid' ? 56 : MOORING_CANVAS.spotHeight;
  for (let row = 1; row <= rows; row += 1) {
    for (let column = 1; column <= columns; column += 1) {
      pushSpace({
        row,
        column,
        x: MOORING_CANVAS.snap + (column - 1) * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap),
        y: MOORING_CANVAS.snap + (row - 1) * (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap),
        width,
        height,
        shape,
      });
    }
  }
  return spaces;
}

export default function EditMooringsForm({
  isOpen,
  teamId,
  team = {},
  memberProfiles = [],
  initialTab = 'map',
  onClose,
}) {
  const { requireStripePayment, stripeGuardDialog } = useStripePaymentGuard(teamId);
  const [tab, setTab] = useState(initialTab || 'map');
  const [areas, setAreas] = useState([]);
  const [spaces, setSpaces] = useState([]);
  const [sailingBoats, setSailingBoats] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [waitlist, setWaitlist] = useState([]);
  const [maintenance, setMaintenance] = useState([]);
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [areaDraft, setAreaDraft] = useState(createAreaDraft);
  const [spaceDraft, setSpaceDraft] = useState(createSpaceDraft);
  const [allocationDraft, setAllocationDraft] = useState(createAllocationDraft);
  const [waitlistDraft, setWaitlistDraft] = useState(createWaitlistDraft);
  const [maintenanceDraft, setMaintenanceDraft] = useState(createMaintenanceDraft);
  const [paymentStatusByAllocationId, setPaymentStatusByAllocationId] = useState({});
  const [memberVisibility, setMemberVisibility] = useState(team?.modules?.sailing?.moorings?.memberVisibility || 'admin_only');
  const [showMemberMap, setShowMemberMap] = useState(Boolean(team?.modules?.sailing?.moorings?.showMemberMap));
  const [allowMemberWaitlistRequests, setAllowMemberWaitlistRequests] = useState(Boolean(team?.modules?.sailing?.moorings?.allowMemberWaitlistRequests));
  const [paymentDraft, setPaymentDraft] = useState(() => createPaymentDraft(team?.modules?.sailing?.moorings?.payment, []));
  const [currency, setCurrency] = useState('GBP');
  const [mapTool, setMapTool] = useState('select');
  const [canvasResizeMode, setCanvasResizeMode] = useState(false);
  const [selectedSpaceId, setSelectedSpaceId] = useState('');
  const [selectedSpaceIds, setSelectedSpaceIds] = useState([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const [waitlistSelectedSpaceId, setWaitlistSelectedSpaceId] = useState('');
  const [selectedWaitlistEntryId, setSelectedWaitlistEntryId] = useState('');
  const [manualWaitlistOpen, setManualWaitlistOpen] = useState(false);
  const [allocationWaitlistSourceId, setAllocationWaitlistSourceId] = useState('');
  const [mapZoom, setMapZoom] = useState(1);
  const [canvasPreview, setCanvasPreview] = useState(null);
  const [spaceSearch, setSpaceSearch] = useState('');
  const [areaPanelOpen, setAreaPanelOpen] = useState(false);
  const [drawingScaleMetres, setDrawingScaleMetres] = useState('1');
  const [dragState, setDragState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [copyDraft, setCopyDraft] = useState(null);
  const [areaDeleteDraft, setAreaDeleteDraft] = useState(null);
  const [inlineLabelDrafts, setInlineLabelDrafts] = useState({});
  const [inlineLabelSavingId, setInlineLabelSavingId] = useState('');
  const [inlineLabelErrors, setInlineLabelErrors] = useState({});
  const [mapDraftDirty, setMapDraftDirty] = useState(false);
  const [mapDraftRevision, setMapDraftRevision] = useState(0);
  const [mapSaveStatus, setMapSaveStatus] = useState('idle');
  const [mapSaveError, setMapSaveError] = useState('');
  const [projectionWarning, setProjectionWarning] = useState('');
  const canvasRef = useRef(null);
  const canvasScrollRef = useRef(null);
  const spaceDraftRef = useRef(spaceDraft);
  const mapDraftDirtyRef = useRef(false);
  const mapDraftRevisionRef = useRef(0);
  const mapSavePromiseRef = useRef(null);
  const flushMapDraftRef = useRef(null);
  const projectionRetryTimersRef = useRef(new Set());

  const membersById = useMemo(() => memberNameById(memberProfiles), [memberProfiles]);
  const memberEmailsById = useMemo(() => memberEmailById(memberProfiles), [memberProfiles]);
  const areasById = useMemo(() => new Map(areas.map((area) => [area.id, area])), [areas]);
  const spacesById = useMemo(() => new Map(spaces.map((space) => [space.id, space])), [spaces]);
  const selectedSpaceIdSet = useMemo(() => new Set(selectedSpaceIds), [selectedSpaceIds]);
  const sailingBoatsById = useMemo(() => new Map(sailingBoats.map((boat) => [boat.id, boat])), [sailingBoats]);
  const sortedSailingBoats = useMemo(() => [...sailingBoats].sort((left, right) => (
    safeSailingBoatLabel(left).localeCompare(safeSailingBoatLabel(right), undefined, { sensitivity: 'base' })
  )), [sailingBoats]);
  const sortedSpaces = useMemo(() => [...spaces].sort((left, right) => {
    const leftArea = areasById.get(left.areaId)?.sortOrder || 0;
    const rightArea = areasById.get(right.areaId)?.sortOrder || 0;
    if (leftArea !== rightArea) return leftArea - rightArea;
    if (Number(left.row || 0) !== Number(right.row || 0)) return Number(left.row || 0) - Number(right.row || 0);
    if (Number(left.column || 0) !== Number(right.column || 0)) return Number(left.column || 0) - Number(right.column || 0);
    return String(left.label || '').localeCompare(String(right.label || ''));
  }), [areasById, spaces]);
  const filteredSpaces = useMemo(() => {
    const needle = cleanText(spaceSearch).toLowerCase();
    if (!needle) return sortedSpaces;
    return sortedSpaces.filter((space) => (
      String(space.label || '').toLowerCase().includes(needle)
      || String(areasById.get(space.areaId)?.name || '').toLowerCase().includes(needle)
      || String(space.status || '').toLowerCase().includes(needle)
    ));
  }, [areasById, sortedSpaces, spaceSearch]);

  const loadStripeSetup = async () => {
    if (!teamId) return;
    const setupSnap = await getDoc(doc(db, 'teams', teamId, 'stripe', 'setup')).catch(() => null);
    if (setupSnap?.exists()) {
      setCurrency(setupSnap.data()?.currency || 'GBP');
    }
  };

  useEffect(() => {
    if (!isOpen) return;
    setTab(initialTab || 'map');
    setMemberVisibility(team?.modules?.sailing?.moorings?.memberVisibility || 'admin_only');
    setShowMemberMap(Boolean(team?.modules?.sailing?.moorings?.showMemberMap));
    setAllowMemberWaitlistRequests(Boolean(team?.modules?.sailing?.moorings?.allowMemberWaitlistRequests));
    setPaymentDraft(createPaymentDraft(team?.modules?.sailing?.moorings?.payment, areas));
  }, [
    areas,
    initialTab,
    isOpen,
    team?.modules?.sailing?.moorings?.allowMemberWaitlistRequests,
    team?.modules?.sailing?.moorings?.memberVisibility,
    team?.modules?.sailing?.moorings?.payment,
    team?.modules?.sailing?.moorings?.showMemberMap,
  ]);

  useEffect(() => {
    if (!isOpen || !teamId) return undefined;
    const unsubscribers = ['mooringAreas', 'mooringSpaces', 'mooringAllocations', 'mooringWaitlist', 'mooringMaintenance', 'sailingBoats'].map((name) => (
      safeOnSnapshot(collection(db, 'teams', teamId, name), (snapshot) => {
        const rows = snapshot.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }));
        if (name === 'mooringAreas') setAreas(rows.sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0)));
        if (name === 'mooringSpaces') setSpaces(rows);
        if (name === 'mooringAllocations') setAllocations(rows);
        if (name === 'mooringWaitlist') setWaitlist(rows);
        if (name === 'mooringMaintenance') setMaintenance(rows);
        if (name === 'sailingBoats') setSailingBoats(rows);
      }, () => setError('Failed to load mooring data.'))
    ));
    loadStripeSetup().catch(() => {});
    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamId]);

  useEffect(() => {
    if (!selectedAreaId && areas.length > 0) {
      setSelectedAreaId(areas[0].id);
      setSpaceDraft((current) => ({ ...current, areaId: areas[0].id }));
    }
  }, [areas, selectedAreaId]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(''), 3500);
    return () => clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    spaceDraftRef.current = spaceDraft;
  }, [spaceDraft]);

  useEffect(() => {
    if (!isOpen || tab !== 'map' || !mapDraftDirty) return undefined;
    const timer = setTimeout(() => flushMapDraftRef.current?.(), 500);
    return () => clearTimeout(timer);
  }, [isOpen, mapDraftDirty, mapDraftRevision, tab]);

  useEffect(() => () => {
    projectionRetryTimersRef.current.forEach((timer) => clearTimeout(timer));
    projectionRetryTimersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!teamId || allocations.length === 0) {
      setPaymentStatusByAllocationId({});
      return undefined;
    }
    let cancelled = false;
    async function loadPaymentStatuses() {
      const entries = await Promise.all(allocations.map(async (allocation) => {
        const space = spacesById.get(allocation.spaceId);
        const boltOnId = allocation.boltOnId || space?.boltOnId || '';
        if (!allocation.memberId || !boltOnId) return [allocation.id, deriveMooringPaymentStatus({ ...allocation, boltOnId })];
        const snap = await getDoc(doc(db, 'teams', teamId, 'members', allocation.memberId, 'boltOns', boltOnId)).catch(() => null);
        return [allocation.id, deriveMooringPaymentStatus({ ...allocation, boltOnId }, snap?.exists() ? snap.data() : null)];
      }));
      if (!cancelled) setPaymentStatusByAllocationId(Object.fromEntries(entries));
    }
    loadPaymentStatuses();
    return () => {
      cancelled = true;
    };
  }, [allocations, spacesById, teamId]);

  if (!isOpen) return null;

  const selectedArea = areasById.get(selectedAreaId) || areas[0] || null;
  const selectedAreaCanvas = canvasFromMooringArea(selectedArea);
  const activeAreaCanvas = canvasPreview && canvasPreview.areaId === selectedArea?.id
    ? canvasPreview.canvas
    : selectedAreaCanvas;
  const areaDraftCanvas = canvasFromMooringArea({
    canvasWidth: areaDraft.canvasWidth,
    canvasHeight: areaDraft.canvasHeight,
    rows: areaDraft.rows,
    columns: areaDraft.columns,
  });
  const selectedAreaSpaces = sortedSpaces.filter((space) => space.areaId === selectedArea?.id);
  const selectedAreaMinimumCanvas = minimumCanvasForMooringSpaces(selectedAreaSpaces, activeAreaCanvas);
  const selectedSpace = spacesById.get(selectedSpaceId) || (spaceDraft.id ? spacesById.get(spaceDraft.id) : null);
  const selectedAllocation = selectedSpace
    ? allocations.find((allocation) => allocation.spaceId === selectedSpace.id && String(allocation.status || 'active') !== 'ended')
    : null;
  const paymentSettings = paymentPayloadFromDraft(paymentDraft);
  const boatClasses = paymentSettings.boatClasses || [];
  const boatClassesById = new Map(boatClasses.map((boatClass) => [boatClass.id, boatClass]));
  const selectedSpacePricePreview = selectedSpace ? mooringPaymentCadences(paymentSettings).map((cadence) => resolveMooringPaymentPrice({
    payment: paymentSettings,
    area: areasById.get(selectedSpace.areaId),
    space: { ...selectedSpace, mooringPrice: pricePayloadFromDraft(spaceDraft.mooringPrice, paymentDraft) },
    allocation: selectedAllocation || {},
    payerType: selectedAllocation?.payerType || 'member',
    cadence,
  })) : [];
  const allocationSpace = spacesById.get(allocationDraft.spaceId);
  const allocationPricePreview = allocationDraft.spaceId ? mooringPaymentCadences(paymentSettings).map((cadence) => resolveMooringPaymentPrice({
    payment: paymentSettings,
    area: areasById.get(allocationSpace?.areaId),
    space: allocationSpace,
    allocation: {
      ...allocationDraft,
      length: parseOptionalNumber(allocationDraft.length),
      customPrices: pricePayloadFromDraft(allocationDraft.customPrices, paymentDraft),
    },
    payerType: allocationDraft.payerType,
    cadence,
  })) : [];
  const draftVisual = visualFromDraft(spaceDraft, activeAreaCanvas);
  const draftMetres = metresFromMooringVisual(draftVisual, drawingScaleMetres, activeAreaCanvas.snap, activeAreaCanvas);
  const drawingScaleNumber = Number(drawingScaleMetres);
  const { symbol, decimals } = getCurrencyMeta(currency);
  const formatMoney = (minor) => `${symbol}${(Number(minor || 0) / Math.pow(10, decimals)).toFixed(decimals)}`;
  const showMonthlyPrices = paymentDraft.cadenceMode === 'monthly' || paymentDraft.cadenceMode === 'payer_choice';
  const showAnnualPrices = paymentDraft.cadenceMode === 'annual' || paymentDraft.cadenceMode === 'payer_choice';
  const payerChoicePriceEntryMode = normalizePayerChoicePriceEntryMode(paymentDraft.payerChoicePriceEntryMode);
  const linkedPayerChoicePrices = paymentDraft.cadenceMode === 'payer_choice' && payerChoicePriceEntryMode !== 'separate';
  const showMonthlyPriceInputs = showMonthlyPrices && (!linkedPayerChoicePrices || payerChoicePriceEntryMode === 'monthly');
  const showAnnualPriceInputs = showAnnualPrices && (!linkedPayerChoicePrices || payerChoicePriceEntryMode === 'annual');

  const setDraftField = (setter, field, value) => setter((current) => ({ ...current, [field]: value }));
  const replaceMapSpaceDraft = (nextDraft, { dirty = false } = {}) => {
    spaceDraftRef.current = nextDraft;
    setSpaceDraft(nextDraft);
    mapDraftDirtyRef.current = dirty;
    setMapDraftDirty(dirty);
    if (!dirty) {
      setMapSaveStatus('idle');
      setMapSaveError('');
    }
  };
  const updateMapSpaceDraft = (updater) => {
    setSpaceDraft((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater;
      spaceDraftRef.current = next;
      return next;
    });
    mapDraftDirtyRef.current = true;
    mapDraftRevisionRef.current += 1;
    setMapDraftDirty(true);
    setMapDraftRevision(mapDraftRevisionRef.current);
    setMapSaveStatus('idle');
    setMapSaveError('');
  };
  const setPaymentField = (field, value) => setPaymentDraft((current) => ({ ...current, [field]: value }));
  const setPaymentPriceField = (group, field, value, areaId = '') => {
    setPaymentDraft((current) => {
      if (group === 'areaPrices' || group === 'areaRates') {
        return {
          ...current,
          [group]: {
            ...(current[group] || {}),
            [areaId]: {
              ...(current[group]?.[areaId] || (group === 'areaRates' ? createSquareRateDraft() : createPriceDraft())),
              [field]: value,
            },
          },
        };
      }
      return {
        ...current,
        [group]: {
          ...(current[group] || {}),
          [field]: value,
        },
      };
    });
  };
  const setSpacePriceField = (field, value) => updateMapSpaceDraft((current) => ({
    ...current,
    mooringPrice: {
      ...(current.mooringPrice || createPriceDraft()),
      [field]: value,
    },
  }));
  const setAllocationPriceField = (field, value) => setAllocationDraft((current) => ({
    ...current,
    customPrices: {
      ...(current.customPrices || createPriceDraft()),
      [field]: value,
    },
  }));
  const addBoatClass = () => setPaymentDraft((current) => ({
    ...current,
    boatClasses: [
      ...(current.boatClasses || []),
      { id: newBoatClassId(), name: '', prices: createPriceDraft() },
    ],
  }));
  const setBoatClassField = (boatClassId, field, value) => setPaymentDraft((current) => ({
    ...current,
    boatClasses: (current.boatClasses || []).map((boatClass) => (
      boatClass.id === boatClassId ? { ...boatClass, [field]: value } : boatClass
    )),
  }));
  const setBoatClassPriceField = (boatClassId, field, value) => setPaymentDraft((current) => ({
    ...current,
    boatClasses: (current.boatClasses || []).map((boatClass) => (
      boatClass.id === boatClassId
        ? { ...boatClass, prices: { ...(boatClass.prices || createPriceDraft()), [field]: value } }
        : boatClass
    )),
  }));
  const removeBoatClass = (boatClassId) => {
    const inUse = allocations.some((allocation) => (
      cleanText(allocation.boatClassId) === boatClassId
      && String(allocation.status || 'active') !== 'ended'
    ));
    if (inUse) {
      setError('Reassign active allocations using this boat class before deleting it.');
      return;
    }
    setPaymentDraft((current) => ({
      ...current,
      boatClasses: (current.boatClasses || []).filter((boatClass) => boatClass.id !== boatClassId),
    }));
  };
  const renderLinkedCadenceNote = ({ memberMonthly, memberAnnual, nonMemberMonthly, nonMemberAnnual, suffix = '' }) => {
    if (!linkedPayerChoicePrices) return null;
    const member = linkedMonthlyAnnualAmounts(memberMonthly, memberAnnual, paymentDraft);
    const nonMember = linkedMonthlyAnnualAmounts(nonMemberMonthly, nonMemberAnnual, paymentDraft);
    const derivedLabel = payerChoicePriceEntryMode === 'annual' ? 'Monthly will be derived from annual' : 'Annual will be derived from monthly';
    const derivedValues = payerChoicePriceEntryMode === 'annual'
      ? `Member ${formatMoney(member.monthly)}${suffix}; non-member ${formatMoney(nonMember.monthly)}${suffix}.`
      : `Member ${formatMoney(member.annual)}${suffix}; non-member ${formatMoney(nonMember.annual)}${suffix}.`;
    return <p className="moorings-form-note">{derivedLabel}: {derivedValues}</p>;
  };
  const renderPriceInputs = (priceDraft, onChange, labelPrefix = '') => (
    <>
      <div className="moorings-price-inputs">
        {showMonthlyPriceInputs && (
          <>
            <label>{labelPrefix}Member monthly<input className="input" type="number" min="0" step="0.01" value={priceDraft.memberMonthlyAmount} onChange={(e) => onChange('memberMonthlyAmount', e.target.value)} /></label>
            <label>{labelPrefix}Non-member monthly<input className="input" type="number" min="0" step="0.01" value={priceDraft.nonMemberMonthlyAmount} onChange={(e) => onChange('nonMemberMonthlyAmount', e.target.value)} /></label>
          </>
        )}
        {showAnnualPriceInputs && (
          <>
            <label>{labelPrefix}Member annual<input className="input" type="number" min="0" step="0.01" value={priceDraft.memberAnnualAmount} onChange={(e) => onChange('memberAnnualAmount', e.target.value)} /></label>
            <label>{labelPrefix}Non-member annual<input className="input" type="number" min="0" step="0.01" value={priceDraft.nonMemberAnnualAmount} onChange={(e) => onChange('nonMemberAnnualAmount', e.target.value)} /></label>
          </>
        )}
      </div>
      {renderLinkedCadenceNote({
        memberMonthly: priceDraft.memberMonthlyAmount,
        memberAnnual: priceDraft.memberAnnualAmount,
        nonMemberMonthly: priceDraft.nonMemberMonthlyAmount,
        nonMemberAnnual: priceDraft.nonMemberAnnualAmount,
      })}
    </>
  );
  const renderRateInputs = (rateDraft, onChange) => (
    <>
      <div className="moorings-price-inputs">
        {showMonthlyPriceInputs && (
          <>
            <label>Member monthly / m<input className="input" type="number" min="0" step="0.01" value={rateDraft.memberMonthlyAmountPerMetre} onChange={(e) => onChange('memberMonthlyAmountPerMetre', e.target.value)} /></label>
            <label>Non-member monthly / m<input className="input" type="number" min="0" step="0.01" value={rateDraft.nonMemberMonthlyAmountPerMetre} onChange={(e) => onChange('nonMemberMonthlyAmountPerMetre', e.target.value)} /></label>
          </>
        )}
        {showAnnualPriceInputs && (
          <>
            <label>Member annual / m<input className="input" type="number" min="0" step="0.01" value={rateDraft.memberAnnualAmountPerMetre} onChange={(e) => onChange('memberAnnualAmountPerMetre', e.target.value)} /></label>
            <label>Non-member annual / m<input className="input" type="number" min="0" step="0.01" value={rateDraft.nonMemberAnnualAmountPerMetre} onChange={(e) => onChange('nonMemberAnnualAmountPerMetre', e.target.value)} /></label>
          </>
        )}
      </div>
      {renderLinkedCadenceNote({
        memberMonthly: rateDraft.memberMonthlyAmountPerMetre,
        memberAnnual: rateDraft.memberAnnualAmountPerMetre,
        nonMemberMonthly: rateDraft.nonMemberMonthlyAmountPerMetre,
        nonMemberAnnual: rateDraft.nonMemberAnnualAmountPerMetre,
        suffix: ' / m',
      })}
    </>
  );
  const renderSquareRateInputs = (rateDraft, onChange) => (
    <>
      <div className="moorings-price-inputs">
        {showMonthlyPriceInputs && (
          <>
            <label>Member monthly / m2<input className="input" type="number" min="0" step="0.01" value={rateDraft.memberMonthlyAmountPerSquareMetre} onChange={(e) => onChange('memberMonthlyAmountPerSquareMetre', e.target.value)} /></label>
            <label>Non-member monthly / m2<input className="input" type="number" min="0" step="0.01" value={rateDraft.nonMemberMonthlyAmountPerSquareMetre} onChange={(e) => onChange('nonMemberMonthlyAmountPerSquareMetre', e.target.value)} /></label>
          </>
        )}
        {showAnnualPriceInputs && (
          <>
            <label>Member annual / m2<input className="input" type="number" min="0" step="0.01" value={rateDraft.memberAnnualAmountPerSquareMetre} onChange={(e) => onChange('memberAnnualAmountPerSquareMetre', e.target.value)} /></label>
            <label>Non-member annual / m2<input className="input" type="number" min="0" step="0.01" value={rateDraft.nonMemberAnnualAmountPerSquareMetre} onChange={(e) => onChange('nonMemberAnnualAmountPerSquareMetre', e.target.value)} /></label>
          </>
        )}
      </div>
      {renderLinkedCadenceNote({
        memberMonthly: rateDraft.memberMonthlyAmountPerSquareMetre,
        memberAnnual: rateDraft.memberAnnualAmountPerSquareMetre,
        nonMemberMonthly: rateDraft.nonMemberMonthlyAmountPerSquareMetre,
        nonMemberAnnual: rateDraft.nonMemberAnnualAmountPerSquareMetre,
        suffix: ' / m2',
      })}
    </>
  );

  const draftSizeFieldsFromVisual = (visual) => {
    const scale = Number(drawingScaleMetres);
    if (!Number.isFinite(scale) || scale <= 0 || visual?.shape === 'label') return {};
    const dimensions = metresFromMooringVisual(visual, scale, selectedAreaCanvas.snap, selectedAreaCanvas);
    return {
      maxLength: dimensions.length > 0 ? dimensions.length.toFixed(1) : '',
      maxBeam: dimensions.beam > 0 ? dimensions.beam.toFixed(1) : '',
    };
  };

  const firestoreSizeFieldsFromVisual = (visual) => {
    const fields = draftSizeFieldsFromVisual(visual);
    if (!Object.keys(fields).length) return {};
    return {
      maxLength: fields.maxLength ? Number(fields.maxLength) : null,
      maxBeam: fields.maxBeam ? Number(fields.maxBeam) : null,
    };
  };

  const setScaledRestrictionField = (field, value) => {
    updateMapSpaceDraft((current) => {
      let next = { ...current, [field]: value };
      const scale = Number(drawingScaleMetres);
      const numericValue = Number(value);
      if (
        Number.isFinite(scale)
        && scale > 0
        && Number.isFinite(numericValue)
        && numericValue > 0
        && (field === 'maxLength' || field === 'maxBeam')
        && current.shape !== 'label'
      ) {
        const visual = visualFromDraft(current, selectedAreaCanvas);
        const nextVisual = mooringVisualFromMetres(visual, {
          length: field === 'maxLength' ? numericValue : undefined,
          beam: field === 'maxBeam' ? numericValue : undefined,
        }, scale, selectedAreaCanvas.snap, selectedAreaCanvas);
        next = draftWithVisual(next, nextVisual);
      }
      return next;
    });
  };

  const setSpaceDraftField = (field, value) => {
    updateMapSpaceDraft((current) => ({ ...current, [field]: value }));
  };

  const setMarkerType = (markerType) => {
    updateMapSpaceDraft((current) => {
      if (markerType === 'label') {
        return {
          ...current,
          status: 'vacant',
          shape: 'label',
          width: String(Math.max(Number(current.width || 0), 96)),
          height: String(Math.max(Number(current.height || 0), 48)),
        };
      }
      return {
        ...current,
        status: current.status || 'vacant',
        shape: current.shape === 'label' ? (selectedArea?.type === 'mooring_field' ? 'circle' : 'rect') : current.shape,
      };
    });
  };

  const canvasForAreaId = (areaId) => (canvasPreview && canvasPreview.areaId === areaId)
    ? canvasPreview.canvas
    : canvasFromMooringArea(areasById.get(areaId) || selectedArea);

  const spacePayloadFromDraft = (draft = spaceDraftRef.current) => {
    const areaId = cleanText(draft.areaId || selectedAreaId);
    if (!areaId) return null;
    const visual = visualFromDraft(draft, canvasForAreaId(areaId));
    const position = rowColumnFromVisual(visual);
    const isLabelMarker = visual.shape === 'label';
    return {
      areaId,
      label: cleanText(draft.label) || uniqueMooringMapLabel(spaces.filter((space) => space.id !== draft.id), isLabelMarker ? 'label' : 'space'),
      row: position.row,
      column: position.column,
      status: isLabelMarker ? 'vacant' : draft.status,
      maxLength: parseOptionalNumber(draft.maxLength),
      maxBeam: parseOptionalNumber(draft.maxBeam),
      maxDraft: parseOptionalNumber(draft.maxDraft),
      vesselTypes: cleanText(draft.vesselTypes).split(',').map(cleanText).filter(Boolean),
      clubBoatId: cleanText(draft.clubBoatId),
      clubBoatLabel: draft.clubBoatId ? safeSailingBoatLabel(sailingBoatsById.get(draft.clubBoatId) || {}) : '',
      boltOnId: cleanText(draft.boltOnId),
      mooringPrice: pricePayloadFromDraft(draft.mooringPrice, paymentDraft),
      notes: cleanText(draft.notes),
      ...visual,
      updatedAt: serverTimestamp(),
    };
  };

  const persistMapDraft = async () => {
    if (mapSavePromiseRef.current) await mapSavePromiseRef.current;
    if (!mapDraftDirtyRef.current) return spaceDraftRef.current;
    const draft = spaceDraftRef.current;
    if (!draft?.id) return draft;
    const payload = spacePayloadFromDraft(draft);
    if (!payload) return null;
    const revision = mapDraftRevisionRef.current;
    setMapSaveStatus('saving');
    setMapSaveError('');
    const savePromise = updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', draft.id), payload)
      .then(() => {
        const savedSpace = { ...(spacesById.get(draft.id) || {}), id: draft.id, ...payload };
        queuePublicSummarySync(areas, spaces.map((space) => space.id === draft.id ? savedSpace : space));
        if (mapDraftRevisionRef.current === revision) {
          mapDraftDirtyRef.current = false;
          setMapDraftDirty(false);
          replaceMapSpaceDraft(createSpaceDraft(savedSpace, canvasForAreaId(savedSpace.areaId)));
          setMapSaveStatus('saved');
        }
        return savedSpace;
      })
      .catch((err) => {
        const message = err?.message || 'Could not save the map changes.';
        setMapSaveStatus('error');
        setMapSaveError(message);
        throw err;
      })
      .finally(() => {
        mapSavePromiseRef.current = null;
      });
    mapSavePromiseRef.current = savePromise;
    return savePromise;
  };

  const flushMapDraft = async () => {
    try {
      let saved = await persistMapDraft();
      if (mapDraftDirtyRef.current) saved = await persistMapDraft();
      return saved;
    } catch {
      return null;
    }
  };
  flushMapDraftRef.current = flushMapDraft;

  const selectSpace = async (space) => {
    if (!space) return false;
    if (space.id !== selectedSpaceId && mapDraftDirtyRef.current && !await flushMapDraft()) return false;
    setSelectedSpaceId(space.id);
    setSelectedSpaceIds([space.id]);
    replaceMapSpaceDraft(createSpaceDraft(space, canvasForAreaId(space.areaId)));
    setMapTool('select');
    return true;
  };

  const clearSpaceSelection = async () => {
    if (mapDraftDirtyRef.current && !await flushMapDraft()) return;
    setSelectedSpaceId('');
    setSelectedSpaceIds([]);
    replaceMapSpaceDraft(createSpaceDraft({ areaId: selectedAreaId }));
  };

  const setPrimarySelectedSpace = async (space, nextIds) => {
    const requestedPrimary = space || spacesById.get(nextIds[nextIds.length - 1]);
    if (requestedPrimary?.id !== selectedSpaceId && mapDraftDirtyRef.current && !await flushMapDraft()) return false;
    const ids = Array.from(new Set(nextIds)).filter((id) => spacesById.has(id));
    setSelectedSpaceIds(ids);
    const primary = space && ids.includes(space.id) ? space : spacesById.get(ids[ids.length - 1]);
    setSelectedSpaceId(primary?.id || '');
    replaceMapSpaceDraft(primary ? createSpaceDraft(primary, canvasForAreaId(primary.areaId)) : createSpaceDraft({ areaId: selectedAreaId }));
    setMapTool('select');
    return true;
  };

  const deleteSelectedSpaces = async () => {
    if (mapDraftDirtyRef.current && !await flushMapDraft()) return;
    const selectedIds = selectedSpaceIds.filter((id) => spacesById.get(id)?.areaId === selectedArea?.id);
    if (selectedIds.length < 2 || busy) return;
    const blockers = selectedSpaceDeleteBlockers({
      selectedIds,
      spaces,
      allocations,
      boats: sailingBoats,
      waitlist,
      maintenance,
    });
    const blockerLabels = [
      blockers.allocations ? `${blockers.allocations} allocation${blockers.allocations === 1 ? '' : 's'}` : '',
      blockers.clubBoats ? `${blockers.clubBoats} linked club boat${blockers.clubBoats === 1 ? '' : 's'}` : '',
      blockers.waitlist ? `${blockers.waitlist} waitlist record${blockers.waitlist === 1 ? '' : 's'}` : '',
      blockers.maintenance ? `${blockers.maintenance} maintenance record${blockers.maintenance === 1 ? '' : 's'}` : '',
    ].filter(Boolean);
    if (blockerLabels.length) {
      setError(`The selected spaces cannot be deleted because they have ${blockerLabels.join(', ')}. Remove or reassign those records first.`);
      return;
    }
    if (!window.confirm(`Delete ${selectedIds.length} selected map items? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    try {
      for (let index = 0; index < selectedIds.length; index += 400) {
        const batch = writeBatch(db);
        selectedIds.slice(index, index + 400).forEach((id) => {
          batch.delete(doc(db, 'teams', teamId, 'mooringSpaces', id));
        });
        await batch.commit();
      }
      queuePublicSummarySync(areas, spaces.filter((space) => !selectedIds.includes(space.id)));
      setSelectedSpaceId('');
      setSelectedSpaceIds([]);
      replaceMapSpaceDraft(createSpaceDraft({ areaId: selectedAreaId }));
      setNotice(`${selectedIds.length} map items deleted.`);
    } catch (err) {
      setError(err?.message || 'Failed to delete the selected spaces.');
    } finally {
      setBusy(false);
    }
  };

  const pointFromCanvasEvent = (event) => {
    const rect = canvasRef.current?.getBoundingClientRect?.();
    if (!rect) return null;
    return {
      x: (event.clientX - rect.left) / mapZoom,
      y: (event.clientY - rect.top) / mapZoom,
    };
  };

  const shapeForMapTool = (tool = mapTool) => (
    tool === 'label' ? 'label' : (selectedArea?.type === 'mooring_field' ? 'circle' : 'rect')
  );

  const statusForMapTool = (tool = mapTool) => (
    tool === 'reserved' ? 'reserved' : tool === 'maintenance' ? 'maintenance' : 'vacant'
  );

  const defaultVisualAtPoint = (point, tool = mapTool) => snapMooringVisual({
    x: point.x - (tool === 'label' ? 120 : MOORING_CANVAS.spotWidth) / 2,
    y: point.y - (tool === 'label' ? 36 : MOORING_CANVAS.spotHeight) / 2,
    width: tool === 'label' ? 120 : MOORING_CANVAS.spotWidth,
    height: tool === 'label' ? 48 : MOORING_CANVAS.spotHeight,
    shape: shapeForMapTool(tool),
    rotation: 0,
    labelPosition: 'inside',
  }, selectedAreaCanvas.snap, selectedAreaCanvas);

  const createSpaceFromVisual = async (visualInput, tool = mapTool) => {
    if (!selectedArea || !visualInput || busy) return;
    const visual = snapMooringVisual({
      ...visualInput,
      shape: visualInput.shape || shapeForMapTool(tool),
      rotation: visualInput.rotation || 0,
      labelPosition: visualInput.labelPosition || 'inside',
    }, selectedAreaCanvas.snap, selectedAreaCanvas);
    const position = rowColumnFromVisual(visual);
    const status = statusForMapTool(tool);
    const label = uniqueMooringMapLabel(spaces, tool === 'label' ? 'label' : 'space');
    setBusy(true);
    setError('');
    try {
      const ref = await addDoc(collection(db, 'teams', teamId, 'mooringSpaces'), {
        areaId: selectedArea.id,
        label,
        row: position.row,
        column: position.column,
        status,
        ...visual,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      queuePublicSummarySync(areas, [...spaces, { id: ref.id, areaId: selectedArea.id, label, status, ...visual, ...position }]);
      setSelectedSpaceId(ref.id);
      setSelectedSpaceIds([ref.id]);
      replaceMapSpaceDraft(createSpaceDraft({ id: ref.id, areaId: selectedArea.id, label, status, ...visual, ...position }, selectedAreaCanvas));
      setMapTool('select');
    } catch (err) {
      setError(err?.message || 'Failed to add space.');
    } finally {
      setBusy(false);
    }
  };

  const createBasicSpace = async () => {
    if (!selectedArea || busy) return;
    const scroll = canvasScrollRef.current;
    const point = scroll
      ? {
        x: (scroll.scrollLeft + scroll.clientWidth / 2) / mapZoom,
        y: (scroll.scrollTop + scroll.clientHeight / 2) / mapZoom,
      }
      : { x: selectedAreaCanvas.width / 2, y: selectedAreaCanvas.height / 2 };
    await createSpaceFromVisual(defaultVisualAtPoint(point, 'vacant'), 'vacant');
  };

  const currentVisualForSpace = (space) => (
    spaceDraft.id === space?.id ? visualFromDraft(spaceDraft, canvasForAreaId(space.areaId)) : normalizeMooringVisual(space, canvasForAreaId(space.areaId))
  );

  const draftFromVisualChange = (current, visual) => ({
    ...draftWithVisual(current, visual),
    ...(visual.shape === 'label' ? {} : draftSizeFieldsFromVisual(visual)),
  });

  const saveSpaceVisual = async (spaceId, visualPatch) => {
    const space = spacesById.get(spaceId);
    if (!space) return;
    const baseVisual = currentVisualForSpace(space);
    const canvas = canvasForAreaId(space.areaId);
    const nextVisual = snapMooringVisual({
      ...baseVisual,
      ...visualPatch,
    }, canvas.snap, canvas);
    const position = rowColumnFromVisual(nextVisual);
    const sizeFields = nextVisual.shape === 'label' ? {} : firestoreSizeFieldsFromVisual(nextVisual);
    setSpaceDraft((current) => {
      const next = current.id === spaceId ? draftFromVisualChange(current, nextVisual) : current;
      spaceDraftRef.current = next;
      return next;
    });
    await updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', spaceId), {
      ...nextVisual,
      ...position,
      ...sizeFields,
      updatedAt: serverTimestamp(),
    });
    queuePublicSummarySync(areas, spaces.map((entry) => entry.id === spaceId ? {
      ...entry,
      ...nextVisual,
      ...position,
      ...sizeFields,
    } : entry));
  };

  const updateDraftVisual = (space, visual) => {
    setSpaceDraft((current) => {
      const next = current.id === space.id ? draftFromVisualChange(current, visual) : current;
      spaceDraftRef.current = next;
      return next;
    });
  };

  const resizedVisualFromDrag = (state, point) => {
    const deltaX = point.x - state.startX;
    const deltaY = point.y - state.startY;
    const next = { ...state.originalVisual };
    if (state.edge.includes('e')) next.width = state.originalVisual.width + deltaX;
    if (state.edge.includes('s')) next.height = state.originalVisual.height + deltaY;
    if (state.edge.includes('w')) {
      next.x = state.originalVisual.x + deltaX;
      next.width = state.originalVisual.width - deltaX;
    }
    if (state.edge.includes('n')) {
      next.y = state.originalVisual.y + deltaY;
      next.height = state.originalVisual.height - deltaY;
    }
    const canvas = canvasForAreaId(state.areaId || spacesById.get(state.spaceId)?.areaId || selectedArea?.id);
    return snapMooringVisual(next, canvas.snap, canvas);
  };

  const canvasFromDrag = (state, point) => {
    const deltaX = point.x - state.startX;
    const deltaY = point.y - state.startY;
    const next = {
      width: state.originalCanvas.width + (state.edge.includes('e') ? deltaX : 0),
      height: state.originalCanvas.height + (state.edge.includes('s') ? deltaY : 0),
    };
    return resizeMooringCanvas(state.originalCanvas, next, selectedAreaSpaces);
  };

  const groupVisualsFromDrag = (state, point) => {
    const canvas = canvasForAreaId(state.areaId);
    const originals = Object.values(state.originalVisuals || {});
    if (!originals.length) return {};
    const snap = canvas.snap || MOORING_CANVAS.snap;
    const requestedX = Math.round((point.x - state.startX) / snap) * snap;
    const requestedY = Math.round((point.y - state.startY) / snap) * snap;
    const minX = Math.max(...originals.map((visual) => -visual.x));
    const maxX = Math.min(...originals.map((visual) => canvas.width - visual.x - visual.width));
    const minY = Math.max(...originals.map((visual) => -visual.y));
    const maxY = Math.min(...originals.map((visual) => canvas.height - visual.y - visual.height));
    const dx = Math.min(maxX, Math.max(minX, requestedX));
    const dy = Math.min(maxY, Math.max(minY, requestedY));
    return Object.fromEntries(Object.entries(state.originalVisuals).map(([id, visual]) => [id, {
      ...visual,
      x: visual.x + dx,
      y: visual.y + dy,
    }]));
  };

  const saveCanvasSize = async (nextCanvas, message = 'Map size saved.') => {
    if (!selectedArea?.id || !nextCanvas || busy) return;
    const resized = resizeMooringCanvas(selectedAreaCanvas, nextCanvas, selectedAreaSpaces);
    const grid = gridDimensionsFromCanvas(resized);
    setBusy(true);
    setError('');
    try {
      await updateDoc(doc(db, 'teams', teamId, 'mooringAreas', selectedArea.id), {
        canvasWidth: resized.width,
        canvasHeight: resized.height,
        rows: grid.rows,
        columns: grid.columns,
        updatedAt: serverTimestamp(),
      });
      setAreaDraft((current) => current.id === selectedArea.id ? {
        ...current,
        canvasWidth: String(resized.width),
        canvasHeight: String(resized.height),
        rows: String(grid.rows),
        columns: String(grid.columns),
      } : current);
      queuePublicSummarySync();
      setNotice(message);
    } catch (err) {
      setError(err?.message || 'Failed to save map size.');
    } finally {
      setBusy(false);
    }
  };

  const resizeSelectedCanvas = async (dw, dh) => {
    await saveCanvasSize({
      width: selectedAreaCanvas.width + dw,
      height: selectedAreaCanvas.height + dh,
    }, 'Map size saved.');
  };

  const handleCanvasResizePointerDown = (event, edge) => {
    if (!selectedArea) return;
    event.preventDefault();
    event.stopPropagation();
    const point = pointFromCanvasEvent(event);
    setCanvasResizeMode(true);
    setDragState({
      mode: 'canvas-resize',
      edge,
      pointerId: event.pointerId,
      startX: point?.x || selectedAreaCanvas.width,
      startY: point?.y || selectedAreaCanvas.height,
      originalCanvas: selectedAreaCanvas,
      areaId: selectedArea.id,
    });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCanvasPointerDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (canvasResizeMode) return;
    const point = pointFromCanvasEvent(event);
    if (!point) return;
    if (mapTool !== 'select') {
      const shape = shapeForMapTool(mapTool);
      const minWidth = mapTool === 'label' ? 96 : MOORING_CANVAS.spotWidth;
      const minHeight = mapTool === 'label' ? 48 : MOORING_CANVAS.spotHeight;
      setDragState({
        mode: 'create',
        pointerId: event.pointerId,
        tool: mapTool,
        startX: point.x,
        startY: point.y,
        currentVisual: defaultVisualAtPoint(point, mapTool),
        shape,
        minWidth,
        minHeight,
        areaId: selectedArea?.id,
      });
      event.currentTarget.setPointerCapture?.(event.pointerId);
      return;
    }
    setDragState({
      mode: 'marquee',
      pointerId: event.pointerId,
      startX: point.x,
      startY: point.y,
      currentX: point.x,
      currentY: point.y,
      areaId: selectedArea?.id,
      additive: event.shiftKey || multiSelectMode,
      initialIds: event.shiftKey || multiSelectMode ? selectedSpaceIds : [],
    });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleSpotPointerDown = async (event, space) => {
    event.preventDefault();
    event.stopPropagation();
    if (mapTool !== 'select') return;
    if (mapDraftDirtyRef.current && !await flushMapDraft()) return;
    if (event.shiftKey) {
      const nextIds = selectedSpaceIdSet.has(space.id)
        ? selectedSpaceIds.filter((id) => id !== space.id)
        : [...selectedSpaceIds, space.id];
      await setPrimarySelectedSpace(selectedSpaceIdSet.has(space.id) ? null : space, nextIds);
      return;
    }
    if (multiSelectMode && !selectedSpaceIdSet.has(space.id)) {
      await setPrimarySelectedSpace(space, [...selectedSpaceIds, space.id]);
      return;
    }
    const moveIds = selectedSpaceIdSet.has(space.id) ? selectedSpaceIds : [space.id];
    if (!selectedSpaceIdSet.has(space.id)) {
      if (!await setPrimarySelectedSpace(space, moveIds)) return;
    } else {
      setSelectedSpaceId(space.id);
    }
    const point = pointFromCanvasEvent(event);
    const originalVisuals = Object.fromEntries(moveIds
      .map((id) => spacesById.get(id))
      .filter((entry) => entry?.areaId === space.areaId)
      .map((entry) => [entry.id, currentVisualForSpace(entry)]));
    setDragState({
      mode: 'group-move',
      spaceId: space.id,
      areaId: space.areaId,
      pointerId: event.pointerId,
      startX: point?.x || 0,
      startY: point?.y || 0,
      originalVisuals,
      previewVisuals: originalVisuals,
    });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleResizePointerDown = async (event, space, edge) => {
    event.preventDefault();
    event.stopPropagation();
    if (!await selectSpace(space)) return;
    const point = pointFromCanvasEvent(event);
    setDragState({
      mode: 'resize',
      edge,
      spaceId: space.id,
      pointerId: event.pointerId,
      startX: point?.x || 0,
      startY: point?.y || 0,
      areaId: space.areaId,
      originalVisual: normalizeMooringVisual(space, canvasForAreaId(space.areaId)),
    });
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleCanvasPointerMove = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const point = pointFromCanvasEvent(event);
    if (!point) return;
    if (dragState.mode === 'canvas-resize') {
      const nextCanvas = canvasFromDrag(dragState, point);
      setCanvasPreview({ areaId: dragState.areaId, canvas: nextCanvas });
      setAreaDraft((current) => current.id === dragState.areaId ? {
        ...current,
        canvasWidth: String(nextCanvas.width),
        canvasHeight: String(nextCanvas.height),
      } : current);
      return;
    }
    if (dragState.mode === 'create') {
      setDragState((current) => current && current.pointerId === event.pointerId ? {
        ...current,
        currentVisual: visualFromMooringDrag(
          { x: current.startX, y: current.startY },
          point,
          {
            shape: current.shape,
            minWidth: current.minWidth,
            minHeight: current.minHeight,
            canvas: canvasForAreaId(current.areaId),
          }
        ),
      } : current);
      return;
    }
    if (dragState.mode === 'marquee') {
      setDragState((current) => current && current.pointerId === event.pointerId ? {
        ...current,
        currentX: point.x,
        currentY: point.y,
      } : current);
      return;
    }
    if (dragState.mode === 'group-move') {
      const previewVisuals = groupVisualsFromDrag(dragState, point);
      const primarySpace = spacesById.get(dragState.spaceId);
      if (primarySpace && previewVisuals[primarySpace.id]) updateDraftVisual(primarySpace, previewVisuals[primarySpace.id]);
      setDragState((current) => current && current.pointerId === event.pointerId ? { ...current, previewVisuals } : current);
      return;
    }
    const space = spacesById.get(dragState.spaceId);
    if (!space) return;
    if (dragState.mode === 'resize') {
      updateDraftVisual(space, resizedVisualFromDrag(dragState, point));
      return;
    }
    const canvas = canvasForAreaId(dragState.areaId || space.areaId);
    const visual = snapMooringVisual({
      ...dragState.originalVisual,
      x: dragState.originalVisual.x + (point.x - dragState.startX),
      y: dragState.originalVisual.y + (point.y - dragState.startY),
    }, canvas.snap, canvas);
    updateDraftVisual(space, visual);
  };

  const handleCanvasPointerUp = async (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    const point = pointFromCanvasEvent(event);
    const state = dragState;
    const space = state.spaceId ? spacesById.get(state.spaceId) : null;
    setDragState(null);
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // Pointer capture may belong to the child spot or handle.
    }
    if (state.mode === 'create') {
      const moved = point && (Math.abs(point.x - state.startX) > 8 || Math.abs(point.y - state.startY) > 8);
      const visual = moved
        ? visualFromMooringDrag(
          { x: state.startX, y: state.startY },
          point,
          { shape: state.shape, minWidth: state.minWidth, minHeight: state.minHeight, canvas: canvasForAreaId(state.areaId) }
        )
        : defaultVisualAtPoint({ x: state.startX, y: state.startY }, state.tool);
      await createSpaceFromVisual(visual, state.tool);
      return;
    }
    if (state.mode === 'marquee' && point) {
      const rect = marqueeRect({ x: state.startX, y: state.startY }, point);
      const moved = rect.width > 6 || rect.height > 6;
      if (!moved) {
        if (!state.additive) clearSpaceSelection();
        return;
      }
      const matchedIds = selectedAreaSpaces.filter((entry) => {
        const visual = normalizeMooringVisual(entry, canvasForAreaId(entry.areaId));
        return rectsIntersect(rect, {
          left: visual.x,
          top: visual.y,
          right: visual.x + visual.width,
          bottom: visual.y + visual.height,
        });
      }).map((entry) => entry.id);
      const nextIds = state.additive ? Array.from(new Set([...(state.initialIds || []), ...matchedIds])) : matchedIds;
      setPrimarySelectedSpace(spacesById.get(nextIds[nextIds.length - 1]), nextIds);
      return;
    }
    if (point && state.mode === 'canvas-resize') {
      setCanvasPreview(null);
      await saveCanvasSize(canvasFromDrag(state, point), 'Map size saved.');
      return;
    }
    if (point && space && state.mode === 'resize') {
      await saveSpaceVisual(state.spaceId, resizedVisualFromDrag(state, point)).catch((err) => {
        setError(err?.message || 'Failed to resize space.');
      });
      return;
    }
    if (point && state.mode === 'group-move') {
      const nextVisuals = groupVisualsFromDrag(state, point);
      const changed = Object.entries(nextVisuals).filter(([id, visual]) => {
        const original = state.originalVisuals[id];
        return original && (visual.x !== original.x || visual.y !== original.y);
      });
      if (!changed.length) return;
      setBusy(true);
      setError('');
      try {
        const batch = writeBatch(db);
        changed.forEach(([id, visual]) => {
          batch.update(doc(db, 'teams', teamId, 'mooringSpaces', id), {
            ...visual,
            ...rowColumnFromVisual(visual),
            updatedAt: serverTimestamp(),
          });
        });
        await batch.commit();
        queuePublicSummarySync(areas, spaces.map((entry) => nextVisuals[entry.id] ? {
          ...entry,
          ...nextVisuals[entry.id],
          ...rowColumnFromVisual(nextVisuals[entry.id]),
        } : entry));
        const primary = spacesById.get(state.spaceId);
        if (primary && nextVisuals[primary.id]) updateDraftVisual(primary, nextVisuals[primary.id]);
        setNotice(`${changed.length} ${changed.length === 1 ? 'space' : 'spaces'} moved.`);
      } catch (err) {
        setError(err?.message || 'Failed to move selected spaces.');
        const primary = spacesById.get(state.spaceId);
        if (primary) replaceMapSpaceDraft(createSpaceDraft(primary, canvasForAreaId(primary.areaId)));
      } finally {
        setBusy(false);
      }
      return;
    }
    if (point && space) {
      await saveSpaceVisual(state.spaceId, {
        ...state.originalVisual,
        x: state.originalVisual.x + (point.x - state.startX),
        y: state.originalVisual.y + (point.y - state.startY),
      }).catch((err) => {
        setError(err?.message || 'Failed to move space.');
      });
    }
  };

  const handleCanvasPointerCancel = (event) => {
    if (!dragState || dragState.pointerId !== event.pointerId) return;
    try {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    } catch {
      // Pointer capture may belong to the child spot or handle.
    }
    setDragState(null);
    setCanvasPreview(null);
  };

  const nudgeSelectedSpace = async (dx, dy) => {
    const space = spacesById.get(selectedSpaceId);
    if (!space) return;
    const visual = currentVisualForSpace(space);
    await saveSpaceVisual(space.id, {
      ...visual,
      x: visual.x + dx,
      y: visual.y + dy,
    }).catch((err) => setError(err?.message || 'Failed to move space.'));
  };

  const resizeSelectedSpace = async (dw, dh) => {
    const space = spacesById.get(selectedSpaceId);
    if (!space) return;
    const visual = currentVisualForSpace(space);
    await saveSpaceVisual(space.id, {
      ...visual,
      width: visual.width + dw,
      height: visual.height + dh,
    }).catch((err) => setError(err?.message || 'Failed to resize space.'));
  };

  const rotateSelectedSpace = async (delta) => {
    const space = spacesById.get(selectedSpaceId);
    if (!space) return;
    const visual = currentVisualForSpace(space);
    await saveSpaceVisual(space.id, {
      ...visual,
      rotation: (visual.rotation + delta + 360) % 360,
    }).catch((err) => setError(err?.message || 'Failed to rotate space.'));
  };

  const fitAreaDraftToSpaces = () => {
    const areaId = areaDraft.id || selectedArea?.id;
    const areaSpaces = sortedSpaces.filter((space) => space.areaId === areaId);
    if (areaSpaces.length === 0) return;
    const fitted = minimumCanvasForMooringSpaces(areaSpaces, selectedAreaCanvas);
    const grid = gridDimensionsFromCanvas({ ...fitted, snap: selectedAreaCanvas.snap });
    setAreaDraft((current) => ({
      ...current,
      canvasWidth: String(fitted.width),
      canvasHeight: String(fitted.height),
      columns: String(grid.columns),
      rows: String(grid.rows),
    }));
  };

  const applyDrawingScaleToRestrictions = () => {
    const visual = visualFromDraft(spaceDraft, selectedAreaCanvas);
    const metresPerSnap = Number(drawingScaleMetres);
    if (!Number.isFinite(metresPerSnap) || metresPerSnap <= 0) {
      setError('Add a valid drawing scale.');
      return;
    }
    const { length, beam } = metresFromMooringVisual(visual, metresPerSnap, selectedAreaCanvas.snap, selectedAreaCanvas);
    updateMapSpaceDraft((current) => ({
      ...current,
      maxLength: length > 0 ? length.toFixed(1) : current.maxLength,
      maxBeam: beam > 0 ? beam.toFixed(1) : current.maxBeam,
    }));
  };

  const openNumberedCopies = async () => {
    const savedSpace = mapDraftDirtyRef.current ? await flushMapDraft() : null;
    if (mapDraftDirtyRef.current && !savedSpace) return;
    const space = savedSpace?.id === selectedSpaceId ? savedSpace : spacesById.get(selectedSpaceId);
    if (!space) return;
    setCopyDraft({
      sourceId: space.id,
      sourceSpace: space,
      ...numberedCopyDefaults(space),
      error: '',
    });
  };

  const createNumberedCopies = async (event) => {
    event.preventDefault();
    const space = copyDraft?.sourceSpace || spacesById.get(copyDraft?.sourceId);
    if (!space) return;
    const count = Number(copyDraft.count);
    const start = Number(copyDraft.start);
    if (!Number.isInteger(count) || count < 1 || count > 100) {
      setCopyDraft((current) => ({ ...current, error: 'Choose between 1 and 100 new spaces.' }));
      return;
    }
    if (!Number.isInteger(start) || start < 0) {
      setCopyDraft((current) => ({ ...current, error: 'Add a valid starting number.' }));
      return;
    }
    const canvas = canvasForAreaId(space.areaId);
    const baseVisual = currentVisualForSpace(space);
    const prefix = String(copyDraft.prefix || '');
    const direction = ['right', 'left', 'down', 'up'].includes(copyDraft.direction) ? copyDraft.direction : 'right';
    const layout = directionalCopyLayout({
      sourceVisual: baseVisual,
      count,
      direction,
      canvas,
    });
    const requiredCanvas = resizeMooringCanvas(canvas, layout.canvas);
    setBusy(true);
    setError('');
    try {
      let lastRef = null;
      let lastCopy = null;
      const createdCopies = [];
      const operations = [];
      const shiftedSpaces = spaces.map((entry) => {
        if (entry.areaId !== space.areaId || (!layout.shiftX && !layout.shiftY)) return entry;
        const shiftedVisual = snapMooringVisual({
          ...normalizeMooringVisual(entry, canvas),
          x: normalizeMooringVisual(entry, canvas).x + layout.shiftX,
          y: normalizeMooringVisual(entry, canvas).y + layout.shiftY,
        }, requiredCanvas.snap, requiredCanvas);
        const shifted = { ...entry, ...shiftedVisual, ...rowColumnFromVisual(shiftedVisual) };
        operations.push((batch) => batch.update(doc(db, 'teams', teamId, 'mooringSpaces', entry.id), {
          ...shiftedVisual,
          ...rowColumnFromVisual(shiftedVisual),
          updatedAt: serverTimestamp(),
        }));
        return shifted;
      });
      layout.visuals.forEach((unsnappedVisual, index) => {
        const visual = snapMooringVisual(unsnappedVisual, requiredCanvas.snap, requiredCanvas);
        const position = rowColumnFromVisual(visual);
        const ref = doc(collection(db, 'teams', teamId, 'mooringSpaces'));
        lastRef = ref;
        const payload = {
          areaId: space.areaId,
          ...copyableMooringSpaceFields(space),
          label: `${prefix}${start + index}`,
          ...visual,
          ...position,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        lastCopy = { id: ref.id, ...payload };
        createdCopies.push(lastCopy);
        operations.push((batch) => batch.set(ref, payload));
      });
      if (requiredCanvas.width !== canvas.width || requiredCanvas.height !== canvas.height) {
        const grid = gridDimensionsFromCanvas(requiredCanvas);
        operations.push((batch) => batch.update(doc(db, 'teams', teamId, 'mooringAreas', space.areaId), {
          canvasWidth: requiredCanvas.width,
          canvasHeight: requiredCanvas.height,
          rows: grid.rows,
          columns: grid.columns,
          updatedAt: serverTimestamp(),
        }));
      }
      for (let index = 0; index < operations.length; index += 400) {
        const batch = writeBatch(db);
        operations.slice(index, index + 400).forEach((operation) => operation(batch));
        await batch.commit();
      }
      const nextAreas = areas.map((area) => area.id === space.areaId ? {
        ...area,
        canvasWidth: requiredCanvas.width,
        canvasHeight: requiredCanvas.height,
        rows: gridDimensionsFromCanvas(requiredCanvas).rows,
        columns: gridDimensionsFromCanvas(requiredCanvas).columns,
      } : area);
      queuePublicSummarySync(nextAreas, [...shiftedSpaces, ...createdCopies]);
      if (lastRef) {
        setSelectedSpaceId(lastRef.id);
        setSelectedSpaceIds([lastRef.id]);
        replaceMapSpaceDraft(createSpaceDraft(lastCopy, requiredCanvas));
      }
      setCopyDraft(null);
      setNotice(`${count} numbered ${count === 1 ? 'space' : 'spaces'} created.`);
    } catch (err) {
      setCopyDraft((current) => ({ ...current, error: err?.message || 'Failed to create numbered spaces.' }));
    } finally {
      setBusy(false);
    }
  };

  const previewAreaDeletion = async (area) => {
    if (!area?.id) return;
    setAreaDeleteDraft({ area, confirmation: '', loading: true, error: '', preview: null });
    try {
      const removeArea = httpsCallable(functions, 'deleteMooringArea');
      const result = await removeArea({ teamId, areaId: area.id, confirm: false });
      setAreaDeleteDraft((current) => current?.area?.id === area.id ? { ...current, loading: false, preview: result.data } : current);
    } catch (err) {
      setAreaDeleteDraft((current) => current?.area?.id === area.id ? { ...current, loading: false, error: err?.message || 'Could not check this area.' } : current);
    }
  };

  const confirmAreaDeletion = async () => {
    const area = areaDeleteDraft?.area;
    if (!area?.id || areaDeleteDraft.confirmation !== area.name || !areaDeleteDraft.preview?.canDelete) return;
    setAreaDeleteDraft((current) => ({ ...current, loading: true, error: '' }));
    setBusy(true);
    try {
      const removeArea = httpsCallable(functions, 'deleteMooringArea');
      const result = await removeArea({ teamId, areaId: area.id, confirm: true });
      setSelectedAreaId(result.data?.nextAreaId || '');
      setSelectedSpaceId('');
      setSelectedSpaceIds([]);
      replaceMapSpaceDraft(createSpaceDraft({ areaId: result.data?.nextAreaId || '' }));
      setAreaDraft(createAreaDraft());
      setAreaPanelOpen(false);
      setAreaDeleteDraft(null);
      setNotice(`“${area.name}” deleted.`);
    } catch (err) {
      setAreaDeleteDraft((current) => ({ ...current, loading: false, error: err?.message || 'Failed to delete this area.' }));
    } finally {
      setBusy(false);
    }
  };

  const saveInlineLabel = async (space, requestedLabel) => {
    const label = cleanText(requestedLabel);
    if (!space?.id || inlineLabelSavingId === space.id) return;
    if (!label) {
      setInlineLabelErrors((current) => ({ ...current, [space.id]: 'A label is required.' }));
      setInlineLabelDrafts((current) => ({ ...current, [space.id]: space.label || '' }));
      return;
    }
    if (label === cleanText(space.label)) return;
    setInlineLabelSavingId(space.id);
    setInlineLabelErrors((current) => ({ ...current, [space.id]: '' }));
    try {
      await updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', space.id), { label, updatedAt: serverTimestamp() });
      queuePublicSummarySync(areas, spaces.map((entry) => entry.id === space.id ? { ...entry, label } : entry));
      setInlineLabelDrafts((current) => ({ ...current, [space.id]: label }));
    } catch (err) {
      setInlineLabelDrafts((current) => ({ ...current, [space.id]: space.label || '' }));
      setInlineLabelErrors((current) => ({ ...current, [space.id]: err?.message || 'Could not save label.' }));
    } finally {
      setInlineLabelSavingId('');
    }
  };

  const saveArea = async (event) => {
    event.preventDefault();
    const name = cleanText(areaDraft.name);
    if (!name) {
      setError('Add an area name.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const requestedCanvas = resizeMooringCanvas(areaDraftCanvas, {
        width: Number(areaDraft.canvasWidth),
        height: Number(areaDraft.canvasHeight),
      }, areaDraft.id ? sortedSpaces.filter((space) => space.areaId === areaDraft.id) : []);
      const grid = gridDimensionsFromCanvas(requestedCanvas);
      const payload = {
        name,
        type: areaDraft.type,
        rows: grid.rows,
        columns: grid.columns,
        canvasWidth: requestedCanvas.width,
        canvasHeight: requestedCanvas.height,
        sortOrder: Number(areaDraft.sortOrder || 0),
        updatedAt: serverTimestamp(),
      };
      if (areaDraft.id) {
        await updateDoc(doc(db, 'teams', teamId, 'mooringAreas', areaDraft.id), payload);
        setSelectedAreaId(areaDraft.id);
      } else {
        const areaRef = await addDoc(collection(db, 'teams', teamId, 'mooringAreas'), { ...payload, createdAt: serverTimestamp() });
        const templateSpaces = createTemplateSpaces({ id: areaRef.id, ...payload }, areaDraft.template);
        await Promise.all(templateSpaces.map((space) => addDoc(collection(db, 'teams', teamId, 'mooringSpaces'), space)));
        setSelectedAreaId(areaRef.id);
      }
      queuePublicSummarySync();
      setAreaDraft(createAreaDraft());
      setAreaPanelOpen(false);
      setNotice('Area saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save area.');
    } finally {
      setBusy(false);
    }
  };

  const saveSpace = async (event) => {
    event.preventDefault();
    const areaId = cleanText(spaceDraft.areaId || selectedAreaId);
    if (!areaId) {
      setError('Choose an area before saving a space.');
      return;
    }
    const payload = spacePayloadFromDraft(spaceDraft);
    setBusy(true);
    setError('');
    try {
      if (spaceDraft.id) {
        await updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', spaceDraft.id), payload);
        const savedSpace = { ...(spacesById.get(spaceDraft.id) || {}), id: spaceDraft.id, ...payload };
        replaceMapSpaceDraft(createSpaceDraft(savedSpace, canvasForAreaId(areaId)));
        queuePublicSummarySync(areas, spaces.map((space) => space.id === spaceDraft.id ? savedSpace : space));
      } else {
        const ref = await addDoc(collection(db, 'teams', teamId, 'mooringSpaces'), { ...payload, createdAt: serverTimestamp() });
        setSelectedSpaceId(ref.id);
        setSelectedSpaceIds([ref.id]);
        const savedSpace = { id: ref.id, ...payload };
        replaceMapSpaceDraft(createSpaceDraft(savedSpace, canvasForAreaId(areaId)));
        queuePublicSummarySync(areas, [...spaces, savedSpace]);
      }
    } catch (err) {
      setError(err?.message || 'Failed to save space.');
    } finally {
      setBusy(false);
    }
  };

  const generateMissingSpaces = async () => {
    if (!selectedArea) return;
    setBusy(true);
    setError('');
    try {
      const existingKeys = new Set(selectedAreaSpaces.map((space) => `${Number(space.row || 0)}:${Number(space.column || 0)}`));
      const creates = [];
      const grid = gridDimensionsFromCanvas(selectedAreaCanvas);
      for (let row = 1; row <= grid.rows; row += 1) {
        for (let column = 1; column <= grid.columns; column += 1) {
          if (!existingKeys.has(`${row}:${column}`)) {
            const visual = snapMooringVisual({
              x: MOORING_CANVAS.snap + (column - 1) * (MOORING_CANVAS.spotWidth + MOORING_CANVAS.snap),
              y: MOORING_CANVAS.snap + (row - 1) * (MOORING_CANVAS.spotHeight + MOORING_CANVAS.snap),
              width: MOORING_CANVAS.spotWidth,
              height: MOORING_CANVAS.spotHeight,
              shape: 'rect',
              rotation: 0,
              labelPosition: 'inside',
            }, selectedAreaCanvas.snap, selectedAreaCanvas);
            creates.push(addDoc(collection(db, 'teams', teamId, 'mooringSpaces'), {
              areaId: selectedArea.id,
              label: `${selectedArea.name}-${row}-${column}`,
              row,
              column,
              status: 'vacant',
              ...visual,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp(),
            }));
          }
        }
      }
      await Promise.all(creates);
      queuePublicSummarySync();
    } catch (err) {
      setError(err?.message || 'Failed to generate spaces.');
    } finally {
      setBusy(false);
    }
  };

  const syncMemberView = async (memberId, nextAllocations = allocations) => {
    if (!memberId) return;
    const mine = nextAllocations.filter((allocation) => allocation.memberId === memberId && String(allocation.status || 'active') !== 'ended');
    const sanitized = mine.map((allocation) => {
      const space = spacesById.get(allocation.spaceId);
      const area = areasById.get(space?.areaId);
      return {
        allocationId: allocation.id,
        status: allocation.status || 'active',
        spaceId: allocation.spaceId || '',
        areaId: space?.areaId || '',
        spaceLabel: space?.label || '',
        areaName: area?.name || '',
        vesselName: allocation.vesselName || '',
        vesselType: allocation.vesselType || '',
        boatClassId: allocation.boatClassId || '',
        boatClassName: boatClassesById.get(allocation.boatClassId)?.name || allocation.boatClassNameSnapshot || '',
        startDate: allocation.startDate || null,
        endDate: allocation.endDate || null,
      };
    });
    await setDoc(doc(db, 'teams', teamId, 'mooringMemberViews', memberId), {
      allocations: sanitized,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  };

  const saveAllocation = async (event) => {
    event.preventDefault();
    if (!allocationDraft.spaceId) {
      setError('Choose a space for the allocation.');
      return;
    }
    if (allocationDraft.payerType === 'member' && !allocationDraft.memberId) {
      setError('Choose the member for this allocation.');
      return;
    }
    if (allocationDraft.payerType === 'non_member' && !cleanText(allocationDraft.payerEmail)) {
      setError('Add the non-member payer email before saving.');
      return;
    }
    const memberName = allocationDraft.memberId
      ? (membersById.get(allocationDraft.memberId) || 'Member')
      : (cleanText(allocationDraft.memberNameSnapshot) || cleanText(allocationDraft.payerEmail) || 'Mooring customer');
    const payerEmail = allocationDraft.payerType === 'non_member'
      ? cleanText(allocationDraft.payerEmail).toLowerCase()
      : cleanText(memberEmailsById.get(allocationDraft.memberId) || allocationDraft.payerEmail).toLowerCase();
    const space = spacesById.get(allocationDraft.spaceId);
    const payload = {
      spaceId: allocationDraft.spaceId,
      memberId: cleanText(allocationDraft.memberId),
      memberNameSnapshot: memberName,
      payerType: allocationDraft.payerType === 'non_member' ? 'non_member' : 'member',
      payerEmail,
      paymentLinkId: cleanText(allocationDraft.paymentLinkId),
      customPrices: paymentSettings.baselineCalculation === 'space'
        ? pricePayloadFromDraft(createPriceDraft())
        : pricePayloadFromDraft(allocationDraft.customPrices, paymentDraft),
      status: allocationDraft.status,
      vesselName: cleanText(allocationDraft.vesselName),
      vesselType: cleanText(allocationDraft.vesselType),
      boatClassId: cleanText(allocationDraft.boatClassId),
      boatClassNameSnapshot: boatClassesById.get(cleanText(allocationDraft.boatClassId))?.name
        || cleanText(allocationDraft.boatClassNameSnapshot),
      length: parseOptionalNumber(allocationDraft.length),
      beam: parseOptionalNumber(allocationDraft.beam),
      draft: parseOptionalNumber(allocationDraft.draft),
      startDate: allocationDraft.startDate || null,
      endDate: allocationDraft.endDate || null,
      boltOnId: cleanText(allocationDraft.boltOnId || space?.boltOnId || ''),
      paymentOverrideStatus: cleanText(allocationDraft.paymentOverrideStatus),
      privateNotes: cleanText(allocationDraft.privateNotes),
      updatedAt: serverTimestamp(),
    };
    const resolved = mooringPaymentCadences(paymentSettings).map((cadence) => resolveMooringPaymentPrice({
      payment: paymentSettings,
      area: areasById.get(space?.areaId),
      space,
      allocation: payload,
      payerType: payload.payerType,
      cadence,
    })).find((entry) => entry.amount > 0);
    if (resolved) {
      payload.resolvedPaymentAmount = resolved.amount;
      payload.resolvedPaymentSource = resolved.source;
      payload.selectedPaymentCadence = resolved.cadence;
    } else {
      payload.resolvedPaymentAmount = 0;
      payload.resolvedPaymentSource = 'none';
      payload.selectedPaymentCadence = '';
    }
    setBusy(true);
    setError('');
    try {
      let allocationId = allocationDraft.id;
      if (allocationId) {
        await updateDoc(doc(db, 'teams', teamId, 'mooringAllocations', allocationId), payload);
      } else {
        const ref = await addDoc(collection(db, 'teams', teamId, 'mooringAllocations'), { ...payload, createdAt: serverTimestamp() });
        allocationId = ref.id;
      }
      await updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', allocationDraft.spaceId), {
        status: allocationDraft.status === 'ended' ? 'vacant' : (allocationDraft.status === 'reserved' ? 'reserved' : 'occupied'),
        updatedAt: serverTimestamp(),
      }).catch(() => {});
      queuePublicSummarySync();
      const nextAllocations = [
        ...allocations.filter((allocation) => allocation.id !== allocationId),
        { id: allocationId, ...payload },
      ];
      if (allocationDraft.memberId) await syncMemberView(allocationDraft.memberId, nextAllocations);
      if (allocationWaitlistSourceId) {
        const allocatedSpace = spacesById.get(allocationDraft.spaceId);
        const allocatedArea = areasById.get(allocatedSpace?.areaId);
        await updateDoc(doc(db, 'teams', teamId, 'mooringWaitlist', allocationWaitlistSourceId), {
          status: 'allocated',
          allocationId,
          allocatedSpaceId: allocationDraft.spaceId,
          offeredSpaceId: allocationDraft.spaceId,
          offeredSpaceLabel: allocatedSpace?.label || '',
          offeredAreaName: allocatedArea?.name || '',
          allocatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        }).catch(() => {});
      }
      setAllocationDraft(createAllocationDraft());
      setAllocationWaitlistSourceId('');
    } catch (err) {
      setError(err?.message || 'Failed to save allocation.');
    } finally {
      setBusy(false);
    }
  };

  const saveWaitlist = async (event) => {
    event.preventDefault();
    if (!waitlistDraft.memberId) {
      setError('Choose a member for the waitlist entry.');
      return;
    }
    const payload = {
      memberId: waitlistDraft.memberId,
      memberNameSnapshot: membersById.get(waitlistDraft.memberId) || 'Member',
      status: waitlistDraft.status,
      requestType: waitlistDraft.requestType || (waitlistDraft.spaceId || waitlistDraft.spaceLabel ? 'space' : 'general'),
      spaceId: cleanText(waitlistDraft.spaceId),
      spaceLabel: cleanText(waitlistDraft.spaceLabel),
      areaName: cleanText(waitlistDraft.areaName),
      offeredSpaceId: cleanText(waitlistDraft.offeredSpaceId),
      offeredSpaceLabel: cleanText(waitlistDraft.offeredSpaceLabel),
      offeredAreaName: cleanText(waitlistDraft.offeredAreaName),
      allocationId: cleanText(waitlistDraft.allocationId),
      allocatedSpaceId: cleanText(waitlistDraft.allocatedSpaceId),
      priority: parseOptionalNumber(waitlistDraft.priority),
      appliedAt: waitlistDraft.appliedAt || localDateValue(new Date()),
      vesselName: cleanText(waitlistDraft.vesselName),
      vesselType: cleanText(waitlistDraft.vesselType),
      proposedBoatClassId: cleanText(waitlistDraft.boatClassId || waitlistDraft.proposedBoatClassId),
      proposedBoatClassNameSnapshot: boatClassesById.get(cleanText(waitlistDraft.boatClassId || waitlistDraft.proposedBoatClassId))?.name
        || cleanText(waitlistDraft.boatClassNameSnapshot || waitlistDraft.proposedBoatClassNameSnapshot),
      length: parseOptionalNumber(waitlistDraft.length),
      beam: parseOptionalNumber(waitlistDraft.beam),
      draft: parseOptionalNumber(waitlistDraft.draft),
      memberNote: cleanText(waitlistDraft.memberNote),
      adminNotes: cleanText(waitlistDraft.adminNotes),
      decisionNote: cleanText(waitlistDraft.decisionNote),
      updatedAt: serverTimestamp(),
    };
    setBusy(true);
    setError('');
    try {
      if (waitlistDraft.id) {
        await updateDoc(doc(db, 'teams', teamId, 'mooringWaitlist', waitlistDraft.id), payload);
      } else {
        await addDoc(collection(db, 'teams', teamId, 'mooringWaitlist'), { ...payload, createdAt: serverTimestamp() });
      }
      setWaitlistDraft(createWaitlistDraft());
      setSelectedWaitlistEntryId('');
    } catch (err) {
      setError(err?.message || 'Failed to save waitlist entry.');
    } finally {
      setBusy(false);
    }
  };

  const saveMaintenance = async (event) => {
    event.preventDefault();
    if (!maintenanceDraft.title) {
      setError('Add a maintenance title.');
      return;
    }
    const payload = {
      spaceId: maintenanceDraft.spaceId,
      type: maintenanceDraft.type,
      status: maintenanceDraft.status,
      title: cleanText(maintenanceDraft.title),
      dueDate: maintenanceDraft.dueDate || null,
      completedAt: maintenanceDraft.completedAt || null,
      notes: cleanText(maintenanceDraft.notes),
      updatedAt: serverTimestamp(),
    };
    setBusy(true);
    setError('');
    try {
      if (maintenanceDraft.id) {
        await updateDoc(doc(db, 'teams', teamId, 'mooringMaintenance', maintenanceDraft.id), payload);
      } else {
        await addDoc(collection(db, 'teams', teamId, 'mooringMaintenance'), { ...payload, createdAt: serverTimestamp() });
      }
      if (payload.spaceId && payload.status !== 'completed') {
        await updateDoc(doc(db, 'teams', teamId, 'mooringSpaces', payload.spaceId), {
          status: 'maintenance',
          updatedAt: serverTimestamp(),
        }).catch(() => {});
        queuePublicSummarySync();
      }
      setMaintenanceDraft(createMaintenanceDraft());
    } catch (err) {
      setError(err?.message || 'Failed to save maintenance entry.');
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        'modules.sailing.moorings.memberVisibility': memberVisibility,
        'modules.sailing.moorings.showMemberMap': Boolean(showMemberMap),
        'modules.sailing.moorings.allowMemberWaitlistRequests': Boolean(allowMemberWaitlistRequests),
      });
      queuePublicSummarySync();
    } catch (err) {
      setError(err?.message || 'Failed to save mooring settings.');
    } finally {
      setBusy(false);
    }
  };

  const savePaymentSettings = async (event) => {
    event.preventDefault();
    const classNames = (paymentDraft.boatClasses || []).map((boatClass) => cleanText(boatClass.name));
    if (classNames.some((name) => !name)) {
      setError('Give every boat class a name before saving.');
      return;
    }
    if (new Set(classNames.map((name) => name.toLocaleLowerCase())).size !== classNames.length) {
      setError('Boat class names must be unique.');
      return;
    }
    if (paymentDraft.baselineCalculation === 'class' && classNames.length === 0) {
      setError('Add at least one boat class before using class pricing.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await updateDoc(doc(db, 'teams', teamId), {
        'modules.sailing.moorings.payment': paymentPayloadFromDraft(paymentDraft),
      });
      setNotice('Mooring payment settings saved.');
    } catch (err) {
      setError(err?.message || 'Failed to save mooring payment settings.');
    } finally {
      setBusy(false);
    }
  };

  const createPaymentLinkForAllocation = async (stripeConfirmed = false) => {
    if (allocationDraft.id && paymentSettings.baselineCalculation === 'class') {
      const savedAllocation = allocations.find((allocation) => allocation.id === allocationDraft.id);
      if (cleanText(savedAllocation?.boatClassId) !== cleanText(allocationDraft.boatClassId)) {
        setError('Save the allocation after changing its boat class before creating a payment link.');
        return;
      }
    }
    if (!stripeConfirmed) {
      await requireStripePayment({
        feature: 'moorings_payments',
        featureLabel: 'Mooring payment links',
        returnTarget: 'moorings_payments',
        onReady: () => createPaymentLinkForAllocation(true),
      });
      return;
    }
    if (!allocationDraft.id) {
      setError('Save the allocation before creating a payment link.');
      return;
    }
    if (paymentSettings.baselineCalculation === 'class') {
      const classId = cleanText(allocationDraft.boatClassId);
      const resolvedClass = boatClassesById.get(classId);
      const hasPrice = resolvedClass && mooringPaymentCadences(paymentSettings).some((cadence) => resolveMooringPaymentPrice({
        payment: paymentSettings,
        area: areasById.get(spacesById.get(allocationDraft.spaceId)?.areaId),
        space: spacesById.get(allocationDraft.spaceId),
        allocation: { ...allocationDraft, customPrices: pricePayloadFromDraft(allocationDraft.customPrices, paymentDraft) },
        payerType: allocationDraft.payerType,
        cadence,
      }).amount > 0);
      if (!hasPrice) {
        setError('Assign a valid, priced boat class before creating a payment link.');
        return;
      }
    }
    const payerEmail = allocationDraft.payerType === 'non_member'
      ? cleanText(allocationDraft.payerEmail).toLowerCase()
      : cleanText(memberEmailsById.get(allocationDraft.memberId) || allocationDraft.payerEmail).toLowerCase();
    if (!payerEmail) {
      setError(allocationDraft.payerType === 'member'
        ? 'This member does not have an email on their profile, so a payment link cannot be email-locked.'
        : 'Add the payer email before creating a payment link.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const linkRef = doc(collection(db, 'teams', teamId, 'mooringPaymentLinks'));
      const space = spacesById.get(allocationDraft.spaceId);
      const area = areasById.get(space?.areaId);
      await setDoc(linkRef, {
        linkId: linkRef.id,
        allocationId: allocationDraft.id,
        payerEmail,
        payerEmailNormalized: payerEmail,
        payerType: allocationDraft.payerType === 'non_member' ? 'non_member' : 'member',
        spaceId: allocationDraft.spaceId || '',
        spaceLabel: space?.label || '',
        areaName: area?.name || '',
        status: 'active',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      await updateDoc(doc(db, 'teams', teamId, 'mooringAllocations', allocationDraft.id), {
        paymentLinkId: linkRef.id,
        payerEmail,
        updatedAt: serverTimestamp(),
      });
      setAllocationDraft((current) => ({ ...current, paymentLinkId: linkRef.id, payerEmail }));
      const url = `${window.location.origin}/moorings/pay/${linkRef.id}?teamId=${encodeURIComponent(teamId)}`;
      await navigator.clipboard?.writeText?.(url).catch(() => {});
      setNotice('Payment link created and copied.');
    } catch (err) {
      setError(err?.message || 'Failed to create payment link.');
    } finally {
      setBusy(false);
    }
  };

  const copyAllocationPaymentLink = async () => {
    if (!allocationDraft.paymentLinkId) return;
    const url = `${window.location.origin}/moorings/pay/${allocationDraft.paymentLinkId}?teamId=${encodeURIComponent(teamId)}`;
    await navigator.clipboard?.writeText?.(url).catch(() => {});
    setNotice('Payment link copied.');
  };

  const deleteRecord = async (collectionName, id, label, afterDelete) => {
    if (collectionName === 'mooringSpaces') {
      if (mapDraftDirtyRef.current && !await flushMapDraft()) return;
      const blockers = selectedSpaceDeleteBlockers({
        selectedIds: [id],
        spaces,
        allocations,
        boats: sailingBoats,
        waitlist,
        maintenance,
      });
      const blockerCount = Object.values(blockers).reduce((total, count) => total + count, 0);
      if (blockerCount > 0) {
        setError('This space cannot be deleted because it has a linked allocation, club boat, waitlist request, or maintenance record. Remove or reassign that record first.');
        return;
      }
    }
    if (!id || !window.confirm(`Delete ${label}?`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteDoc(doc(db, 'teams', teamId, collectionName, id));
      await afterDelete?.();
      if (collectionName === 'mooringSpaces') queuePublicSummarySync(areas, spaces.filter((space) => space.id !== id));
    } catch (err) {
      setError(err?.message || `Failed to delete ${label}.`);
    } finally {
      setBusy(false);
    }
  };

  const syncPublicSummary = async (currentAreas = areas, sourceSpaces = spaces) => {
    if (!teamId) return;
    const showClubBoatNames = team?.modules?.sailing?.boats?.showClubBoatNamesOnMooringMap === true;
    const currentSpaces = sourceSpaces.map((space) => {
      const data = { ...space };
      if (data.clubBoatId) {
        return {
          ...data,
          clubBoatLabel: showClubBoatNames ? (data.clubBoatLabel || 'Club boat') : 'Club boat',
        };
      }
      return data;
    });
    const summary = summarizeMoorings({ spaces: currentSpaces });
    const publicMap = buildMooringPublicMapView(currentAreas, currentSpaces);
    await Promise.all([
      setDoc(doc(db, 'teams', teamId, 'mooringPublicViews', 'summary'), {
        total: summary.total,
        vacant: summary.vacant,
        occupied: summary.occupied,
        reserved: summary.reserved,
        maintenanceBlocked: summary.maintenanceBlocked,
        areas: currentAreas.length,
        updatedAt: serverTimestamp(),
      }, { merge: true }),
      setDoc(doc(db, 'teams', teamId, 'mooringPublicViews', 'map'), {
        ...publicMap,
        updatedAt: serverTimestamp(),
      }, { merge: true }),
    ]);
  };

  const queuePublicSummarySync = (nextAreas = areas, nextSpaces = spaces, attempt = 0) => {
    syncPublicSummary(nextAreas, nextSpaces).then(() => {
      setProjectionWarning('');
    }).catch(() => {
      if (attempt < 2) {
        const timer = setTimeout(() => {
          projectionRetryTimersRef.current.delete(timer);
          queuePublicSummarySync(nextAreas, nextSpaces, attempt + 1);
        }, 750 * (attempt + 1));
        projectionRetryTimersRef.current.add(timer);
        return;
      }
      setProjectionWarning('Your mooring change was saved, but the member-facing map is still syncing. Retry when your connection is stable.');
    });
  };

  const handleEditorClose = async () => {
    if (mapDraftDirtyRef.current && !await flushMapDraft()) return;
    onClose?.();
  };

  const closeWithoutMapDraft = () => {
    mapDraftDirtyRef.current = false;
    setMapDraftDirty(false);
    setMapSaveError('');
    setMapSaveStatus('idle');
    onClose?.();
  };

  const handleTabChange = async (nextTab) => {
    if (nextTab === tab) return;
    if (tab === 'map' && mapDraftDirtyRef.current && !await flushMapDraft()) return;
    if (nextTab === 'payments') {
      await requireStripePayment({
        feature: 'moorings_payments',
        featureLabel: 'Mooring payments',
        returnTarget: 'moorings_payments',
        onReady: () => setTab(nextTab),
      });
      return;
    }
    setTab(nextTab);
  };

  const selectedWaitlistSuitability = suggestMooringSpaces(spaces, {
    type: waitlistDraft.vesselType,
    length: waitlistDraft.length,
    beam: waitlistDraft.beam,
    draft: waitlistDraft.draft,
  }).slice(0, 5);
  const waitlistSelectedSpace = spacesById.get(waitlistSelectedSpaceId) || null;
  const waitlistSelectedArea = areasById.get(waitlistSelectedSpace?.areaId) || selectedArea;
  const selectedWaitlistEntry = waitlist.find((entry) => entry.id === selectedWaitlistEntryId) || null;
  const openWaitlistEntries = sortMooringWaitlist(waitlist.filter(isMooringWaitlistOpen));
  const generalWaitlistEntries = openWaitlistEntries.filter((entry) => !cleanText(entry.spaceId || entry.spaceLabel || entry.offeredSpaceId || entry.offeredSpaceLabel));
  const visualWaitlistQueue = waitlistSelectedSpace
    ? waitlistQueueForSpace(waitlist, waitlistSelectedSpace, waitlistSelectedArea)
    : generalWaitlistEntries.map((entry) => ({ ...entry, matchType: 'general' }));
  const waitlistCountsBySpaceId = new Map();
  selectedAreaSpaces.forEach((space) => {
    const area = areasById.get(space.areaId);
    const count = openWaitlistEntries.filter((entry) => mooringWaitlistSpaceMatches(entry, space, area)).length;
    if (count > 0) waitlistCountsBySpaceId.set(space.id, count);
  });
  const selectedWaitlistRequestSpace = selectedWaitlistEntry
    ? (spacesById.get(selectedWaitlistEntry.spaceId) || spacesById.get(selectedWaitlistEntry.offeredSpaceId) || null)
    : null;
  const waitlistActionSpace = waitlistSelectedSpace || selectedWaitlistRequestSpace;
  const selectedWaitlistDimensions = [
    selectedWaitlistEntry?.length ? `${selectedWaitlistEntry.length}m L` : '',
    selectedWaitlistEntry?.beam ? `${selectedWaitlistEntry.beam}m beam` : '',
    selectedWaitlistEntry?.draft ? `${selectedWaitlistEntry.draft}m draft` : '',
  ].filter(Boolean).join(' · ');

  const openWaitlistEntryDetail = (entry, space = null) => {
    if (!entry) return;
    setSelectedWaitlistEntryId(entry.id);
    if (space?.id) setWaitlistSelectedSpaceId(space.id);
    setWaitlistDraft(createWaitlistDraft(entry));
  };

  const offerWaitlistSpace = async (entry = selectedWaitlistEntry, space = waitlistActionSpace) => {
    if (!entry?.id || !space?.id) {
      setError('Choose a waitlist entry and mooring space first.');
      return;
    }
    if (paymentSettings.baselineCalculation === 'class' && !boatClassesById.has(cleanText(waitlistDraft.boatClassId))) {
      setError('Choose a valid boat class before offering this space.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const offerSpace = httpsCallable(functions, 'offerMooringWaitlistSpace');
      await offerSpace({
        teamId,
        entryId: entry.id,
        spaceId: space.id,
        boatClassId: cleanText(waitlistDraft.boatClassId),
        decisionNote: cleanText(waitlistDraft.decisionNote || waitlistDraft.adminNotes),
      });
      queuePublicSummarySync();
      setNotice('Space offered and member notified.');
      setSelectedWaitlistEntryId('');
      setWaitlistDraft(createWaitlistDraft());
    } catch (err) {
      setError(err?.message || 'Failed to offer this mooring space.');
    } finally {
      setBusy(false);
    }
  };

  const allocateWaitlistEntry = (entry = selectedWaitlistEntry, space = waitlistActionSpace) => {
    if (!entry?.id || !space?.id) {
      setError('Choose a waitlist entry and mooring space first.');
      return;
    }
    const today = localDateValue(new Date());
    setAllocationDraft(createAllocationDraft({
      spaceId: space.id,
      memberId: entry.memberId || '',
      memberNameSnapshot: entry.memberNameSnapshot || membersById.get(entry.memberId) || '',
      payerType: 'member',
      status: 'active',
      vesselName: entry.vesselName || '',
      vesselType: entry.vesselType || '',
      boatClassId: cleanText(waitlistDraft.boatClassId || entry.boatClassId || entry.proposedBoatClassId),
      boatClassNameSnapshot: boatClassesById.get(cleanText(waitlistDraft.boatClassId || entry.boatClassId || entry.proposedBoatClassId))?.name
        || entry.boatClassNameSnapshot
        || entry.proposedBoatClassNameSnapshot
        || '',
      length: entry.length,
      beam: entry.beam,
      draft: entry.draft,
      startDate: today,
    }));
    setAllocationWaitlistSourceId(entry.id);
    setSelectedWaitlistEntryId('');
    setWaitlistDraft(createWaitlistDraft());
    setTab('allocations');
    setNotice('Allocation prefilled from the waitlist request.');
  };

  const withdrawWaitlistEntry = async (entry = selectedWaitlistEntry) => {
    if (!entry?.id) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await updateDoc(doc(db, 'teams', teamId, 'mooringWaitlist', entry.id), {
        status: 'withdrawn',
        decisionNote: cleanText(waitlistDraft.decisionNote || waitlistDraft.adminNotes),
        updatedAt: serverTimestamp(),
      });
      setNotice('Waitlist request marked withdrawn.');
      setSelectedWaitlistEntryId('');
      setWaitlistDraft(createWaitlistDraft());
    } catch (err) {
      setError(err?.message || 'Failed to update waitlist request.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="moorings-editor">
      <div className="moorings-editor__header">
        <div>
          <h2 className="modal-title">Moorings</h2>
          <p className="text-muted">Manage club-only mooring maps, allocations, waitlists, maintenance, and mooring payment links.</p>
        </div>
        <button type="button" className="btn btn--secondary btn--sm" onClick={handleEditorClose}>Close</button>
      </div>

      {error && <ErrorMessage message={error} />}
      {projectionWarning && (
        <div className="moorings-sync-warning" role="alert">
          <span>{projectionWarning}</span>
          <button type="button" className="btn btn--ghost btn--xs" onClick={() => queuePublicSummarySync()}>Retry sync</button>
        </div>
      )}
      {mapSaveStatus === 'error' && (
        <div className="moorings-sync-warning" role="alert">
          <span>{mapSaveError || 'Could not save the selected map item.'}</span>
          <button type="button" className="btn btn--secondary btn--xs" onClick={() => flushMapDraft()}>Retry save</button>
          <button type="button" className="btn btn--ghost btn--xs" onClick={closeWithoutMapDraft}>Close without saving</button>
        </div>
      )}
      {notice && (
        <div className="moorings-notice" role="status" aria-live="polite">
          <span>{notice}</span>
          <button type="button" aria-label="Dismiss notice" onClick={() => setNotice('')}>x</button>
        </div>
      )}

      {copyDraft && (
        <div className="moorings-action-backdrop" role="presentation" onClick={() => !busy && setCopyDraft(null)}>
          <section className="moorings-action-dialog" role="dialog" aria-modal="true" aria-labelledby="moorings-copy-title" onClick={(event) => event.stopPropagation()}>
            <div className="moorings-action-dialog__header">
              <div><h3 id="moorings-copy-title">Create numbered copies</h3><p>Copy the selected space’s setup without copying its allocation, payment, or club boat.</p></div>
              <button type="button" className="btn btn--ghost btn--xs" onClick={() => setCopyDraft(null)} disabled={busy}>Close</button>
            </div>
            <form className="modal-form" onSubmit={createNumberedCopies}>
              <div className="moorings-form-row">
                <label>New spaces<input className="input" type="number" min="1" max="100" value={copyDraft.count} onChange={(event) => setCopyDraft((current) => ({ ...current, count: event.target.value, error: '' }))} /></label>
                <label>Label prefix<input className="input" value={copyDraft.prefix} onChange={(event) => setCopyDraft((current) => ({ ...current, prefix: event.target.value, error: '' }))} placeholder="Dinghy " /></label>
                <label>Starting number<input className="input" type="number" min="0" value={copyDraft.start} onChange={(event) => setCopyDraft((current) => ({ ...current, start: event.target.value, error: '' }))} /></label>
              </div>
              <label>Placement<select className="input" value={copyDraft.direction} onChange={(event) => setCopyDraft((current) => ({ ...current, direction: event.target.value }))}><option value="right">Right</option><option value="left">Left</option><option value="down">Down</option><option value="up">Up</option></select></label>
              <p className="moorings-form-note">The map grows automatically if the new spaces extend beyond its current edge. Maximum 100 per operation.</p>
              {copyDraft.error && <div className="moorings-inline-error" role="alert">{copyDraft.error}</div>}
              <div className="form-actions">
                <SubmitButton type="submit" variant="primary" label={busy ? 'Creating…' : 'Create copies'} disabled={busy} />
                <SubmitButton type="button" variant="ghost" label="Cancel" onClick={() => setCopyDraft(null)} disabled={busy} />
              </div>
            </form>
          </section>
        </div>
      )}

      {areaDeleteDraft && (
        <div className="moorings-action-backdrop" role="presentation" onClick={() => !areaDeleteDraft.loading && setAreaDeleteDraft(null)}>
          <section className="moorings-action-dialog moorings-action-dialog--danger" role="dialog" aria-modal="true" aria-labelledby="moorings-delete-title" onClick={(event) => event.stopPropagation()}>
            <div className="moorings-action-dialog__header">
              <div><h3 id="moorings-delete-title">Delete “{areaDeleteDraft.area.name}”?</h3><p>Paid mooring areas can never be deleted.</p></div>
              <button type="button" className="btn btn--ghost btn--xs" onClick={() => setAreaDeleteDraft(null)} disabled={areaDeleteDraft.loading}>Close</button>
            </div>
            {areaDeleteDraft.loading && !areaDeleteDraft.preview ? <p className="text-muted">Checking payments and linked records…</p> : null}
            {areaDeleteDraft.preview && (
              <>
                <dl className="moorings-delete-counts">
                  <div><dt>Spaces</dt><dd>{areaDeleteDraft.preview.counts?.spaces || 0}</dd></div>
                  <div><dt>Unpaid allocations</dt><dd>{areaDeleteDraft.preview.counts?.allocations || 0}</dd></div>
                  <div><dt>Payment links</dt><dd>{areaDeleteDraft.preview.counts?.paymentLinks || 0}</dd></div>
                  <div><dt>Pending checkouts</dt><dd>{areaDeleteDraft.preview.counts?.pendingCheckouts || 0}</dd></div>
                  <div><dt>Maintenance entries</dt><dd>{areaDeleteDraft.preview.counts?.maintenanceEntries || 0}</dd></div>
                  <div><dt>Waitlist entries</dt><dd>{areaDeleteDraft.preview.counts?.waitlistEntries || 0}</dd></div>
                  <div><dt>Linked club boats</dt><dd>{areaDeleteDraft.preview.counts?.linkedClubBoats || 0}</dd></div>
                </dl>
                {areaDeleteDraft.preview.canDelete ? (
                  <>
                    <p className="moorings-danger-note">These unpaid setup records will be permanently removed. Linked club boats will be kept and detached from their spaces.</p>
                    <label>Type <strong>{areaDeleteDraft.area.name}</strong> to confirm<input className="input" value={areaDeleteDraft.confirmation} onChange={(event) => setAreaDeleteDraft((current) => ({ ...current, confirmation: event.target.value }))} autoComplete="off" /></label>
                  </>
                ) : (
                  <p className="moorings-danger-note" role="alert">This area has {areaDeleteDraft.preview.blockers?.paid || 0} paid payment record(s) and cannot be deleted.</p>
                )}
              </>
            )}
            {areaDeleteDraft.error && <div className="moorings-inline-error" role="alert">{areaDeleteDraft.error}</div>}
            <div className="form-actions">
              {areaDeleteDraft.preview?.canDelete && <SubmitButton type="button" variant="secondary" label={areaDeleteDraft.loading ? 'Deleting…' : 'Delete area'} onClick={confirmAreaDeletion} disabled={areaDeleteDraft.loading || areaDeleteDraft.confirmation !== areaDeleteDraft.area.name} />}
              <SubmitButton type="button" variant="ghost" label="Cancel" onClick={() => setAreaDeleteDraft(null)} disabled={areaDeleteDraft.loading} />
            </div>
          </section>
        </div>
      )}

      <div className="moorings-editor__tabs" role="tablist" aria-label="Moorings sections">
        {[
          ['map', 'Map'],
          ['spaces', 'Spaces'],
          ['allocations', 'Allocations'],
          ['payments', 'Payments'],
          ['waitlist', 'Waitlist'],
          ['maintenance', 'Maintenance'],
          ['settings', 'Settings'],
        ].map(([key, label]) => (
          <button key={key} type="button" className={tab === key ? 'active' : ''} onClick={() => handleTabChange(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'map' && (
        <div className="moorings-map-builder">
          <section className="moorings-map-workspace">
            <div className="moorings-map-topbar">
              <div className="moorings-area-tabs" aria-label="Mooring map areas">
                {areas.map((area) => (
                  <button key={area.id} type="button" className={area.id === selectedArea?.id ? 'active' : ''} onClick={async () => {
                    if (mapDraftDirtyRef.current && !await flushMapDraft()) return;
                    setSelectedAreaId(area.id);
                    setSelectedSpaceId('');
                    setSelectedSpaceIds([]);
                    replaceMapSpaceDraft(createSpaceDraft({ areaId: area.id }));
                  }}>
                    {area.name}
                  </button>
                ))}
                <button type="button" className="moorings-area-tabs__manage" onClick={() => {
                  setAreaDraft(createAreaDraft(selectedArea || {}));
                  setAreaPanelOpen((open) => !open);
                }}>
                  Manage areas
                </button>
                <button type="button" className="moorings-area-tabs__manage" onClick={() => {
                  setAreaDraft(createAreaDraft({ template: 'blank' }));
                  setAreaPanelOpen(true);
                }}>
                  New area
                </button>
              </div>
              <div className="moorings-map-zoom">
                {selectedArea && (
                  <button
                    type="button"
                    className={`btn btn--ghost btn--xs${canvasResizeMode ? ' active' : ''}`}
                    onClick={() => {
                      setCanvasResizeMode((value) => !value);
                      setMapTool('select');
                    }}
                  >
                    Resize map
                  </button>
                )}
                {selectedArea && <button type="button" className="btn btn--ghost btn--xs" onClick={generateMissingSpaces} disabled={busy}>Fill grid</button>}
                <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMapZoom((value) => Math.max(0.6, Number((value - 0.1).toFixed(1))))}>-</button>
                <span>{Math.round(mapZoom * 100)}%</span>
                <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMapZoom((value) => Math.min(1.6, Number((value + 0.1).toFixed(1))))}>+</button>
              </div>
            </div>

            <div className="moorings-map-toolbar" aria-label="Map tools">
              {selectedArea && (
                <button type="button" onClick={createBasicSpace} disabled={busy}>
                  Add basic space
                </button>
              )}
              {selectedArea && (
                <button type="button" className={multiSelectMode ? 'active' : ''} onClick={() => setMultiSelectMode((value) => !value)} disabled={busy} aria-pressed={multiSelectMode}>
                  Multi-select
                </button>
              )}
              {selectedSpaceIds.length > 1 && (
                <button type="button" onClick={clearSpaceSelection} disabled={busy}>
                  Clear {selectedSpaceIds.length} selected
                </button>
              )}
              {selectedSpaceIds.length > 1 && (
                <button type="button" className="moorings-danger-action" onClick={deleteSelectedSpaces} disabled={busy}>
                  Delete {selectedSpaceIds.length} selected
                </button>
              )}
              {MAP_TOOLS.map(([tool, label]) => (
                <button key={tool} type="button" className={mapTool === tool ? 'active' : ''} onClick={() => {
                  setMapTool(tool);
                  if (tool !== 'select') setCanvasResizeMode(false);
                }}>
                  {label}
                </button>
              ))}
              <span className="moorings-map-toolbar__hint">
                {canvasResizeMode
                  ? 'Drag the map edge or corner handles to change the drawing area.'
                  : (mapTool === 'select'
                    ? (multiSelectMode ? 'Tap spaces to add them, drag selected spaces together, or drag the map to select an area.' : 'Shift-click or drag the map to select multiple spaces, then drag the group.')
                    : 'Click-drag on the map to draw.')}
              </span>
            </div>

            {selectedArea && canvasResizeMode && (
              <div className="moorings-canvas-size-controls" aria-label="Map size controls">
                <span>Map size: {Math.round(activeAreaCanvas.width)} x {Math.round(activeAreaCanvas.height)}</span>
                <button type="button" onClick={() => resizeSelectedCanvas(MOORING_CANVAS.snap, 0)} disabled={busy}>Wider</button>
                <button type="button" onClick={() => resizeSelectedCanvas(-MOORING_CANVAS.snap, 0)} disabled={busy || activeAreaCanvas.width <= selectedAreaMinimumCanvas.width}>Narrower</button>
                <button type="button" onClick={() => resizeSelectedCanvas(0, MOORING_CANVAS.snap)} disabled={busy}>Taller</button>
                <button type="button" onClick={() => resizeSelectedCanvas(0, -MOORING_CANVAS.snap)} disabled={busy || activeAreaCanvas.height <= selectedAreaMinimumCanvas.height}>Shorter</button>
                <button type="button" onClick={() => saveCanvasSize(selectedAreaMinimumCanvas, 'Map fitted around spaces.')} disabled={busy || selectedAreaSpaces.length === 0}>Fit around spaces</button>
              </div>
            )}

            {areaPanelOpen && (
              <section className="moorings-panel moorings-area-manager">
                <div className="moorings-area-manager__header">
                  <h3>{areaDraft.id ? 'Edit area' : 'Add area'}</h3>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => {
                    setAreaDraft(createAreaDraft());
                    setAreaPanelOpen(false);
                  }}>Close</button>
                </div>
                <form onSubmit={saveArea} className="modal-form">
                  <label>Name<input className="input" value={areaDraft.name} onChange={(e) => setDraftField(setAreaDraft, 'name', e.target.value)} /></label>
                  <label>Type<select className="input" value={areaDraft.type} onChange={(e) => setDraftField(setAreaDraft, 'type', e.target.value)}>{AREA_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                  <label>Area order<input className="input" type="number" value={areaDraft.sortOrder} onChange={(e) => setDraftField(setAreaDraft, 'sortOrder', e.target.value)} /><small>Controls the area tabs; lower numbers appear first.</small></label>
                  <details className="moorings-advanced-fields">
                    <summary>Advanced area setup</summary>
                    <p className="moorings-advanced-help">
                      The visible map boundary is resized on the canvas. These fields are mainly for setup and compatibility with older grid maps.
                    </p>
                    {!areaDraft.id && <label>Starting layout<select className="input" value={areaDraft.template} onChange={(e) => setDraftField(setAreaDraft, 'template', e.target.value)}>{AREA_TEMPLATES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>}
                    <div className="moorings-form-row">
                      <label>Canvas width<input className="input" type="number" min={MOORING_CANVAS_MIN.width} step={MOORING_CANVAS.snap} value={areaDraft.canvasWidth} onChange={(e) => setDraftField(setAreaDraft, 'canvasWidth', e.target.value)} /><small>Pixels</small></label>
                      <label>Canvas height<input className="input" type="number" min={MOORING_CANVAS_MIN.height} step={MOORING_CANVAS.snap} value={areaDraft.canvasHeight} onChange={(e) => setDraftField(setAreaDraft, 'canvasHeight', e.target.value)} /><small>Pixels</small></label>
                    </div>
                    <div className="moorings-area-size-preview">
                      <span>Canvas: {areaDraftCanvas.width}px x {areaDraftCanvas.height}px</span>
                      <button type="button" className="btn btn--ghost btn--xs" onClick={fitAreaDraftToSpaces} disabled={busy || !(areaDraft.id || selectedArea?.id)}>
                        Fit around spaces
                      </button>
                    </div>
                  </details>
                  <div className="form-actions moorings-area-manager__actions">
                    <SubmitButton type="submit" variant="primary" label={areaDraft.id ? 'Save area' : 'Add area'} disabled={busy} />
                    <SubmitButton type="button" variant="ghost" label="New area" onClick={() => setAreaDraft(createAreaDraft())} disabled={busy} />
                  </div>
                </form>
                {areas.length > 0 && (
                  <section className="moorings-area-manager__existing">
                    <h4>Existing areas</h4>
                    <div className="moorings-list moorings-list--compact">
                      {areas.map((area) => (
                        <div key={area.id} className="moorings-list-row">
                          <span><strong>{area.name}</strong><small>{AREA_TYPES.find(([value]) => value === area.type)?.[1] || area.type}</small></span>
                          <button type="button" className="btn btn--ghost btn--xs" onClick={() => {
                            setSelectedAreaId(area.id);
                            setAreaDraft(createAreaDraft(area));
                          }}>Edit</button>
                          <button type="button" className="btn btn--ghost btn--xs moorings-danger-action" onClick={() => previewAreaDeletion(area)} disabled={busy}>Delete</button>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </section>
            )}

            {selectedArea ? (
              <div className="moorings-map-scroll" ref={canvasScrollRef}>
                <div
                  ref={canvasRef}
                  className={`moorings-canvas moorings-canvas--tool-${mapTool}${multiSelectMode ? ' is-multi-selecting' : ''}`}
                  style={{
                    width: activeAreaCanvas.width * mapZoom,
                    height: activeAreaCanvas.height * mapZoom,
                    '--moorings-zoom': mapZoom,
                  }}
                  onPointerDown={handleCanvasPointerDown}
                  onPointerMove={handleCanvasPointerMove}
                  onPointerUp={handleCanvasPointerUp}
                  onPointerCancel={handleCanvasPointerCancel}
                >
                  <div className={`moorings-canvas-boundary${canvasResizeMode ? ' is-resizing' : ''}`} aria-hidden="true" />
                  <div className="moorings-canvas__area-name">{selectedArea.name}</div>
                  <div className="moorings-canvas__size-label">
                    {Math.round(activeAreaCanvas.width)} x {Math.round(activeAreaCanvas.height)}
                  </div>
                  {canvasResizeMode && (
                    <>
                      <button
                        type="button"
                        className="moorings-canvas-size-handle moorings-canvas-size-handle--e"
                        aria-label="Resize map width"
                        onPointerDown={(event) => handleCanvasResizePointerDown(event, 'e')}
                      />
                      <button
                        type="button"
                        className="moorings-canvas-size-handle moorings-canvas-size-handle--s"
                        aria-label="Resize map height"
                        onPointerDown={(event) => handleCanvasResizePointerDown(event, 's')}
                      />
                      <button
                        type="button"
                        className="moorings-canvas-size-handle moorings-canvas-size-handle--se"
                        aria-label="Resize map width and height"
                        onPointerDown={(event) => handleCanvasResizePointerDown(event, 'se')}
                      />
                    </>
                  )}
                  {dragState?.mode === 'create' && dragState.currentVisual && (
                    <div
                      className={`moorings-canvas-spot moorings-canvas-spot--${statusForMapTool(dragState.tool)} moorings-canvas-spot--${dragState.currentVisual.shape} moorings-canvas-spot--preview`}
                      style={{
                        left: dragState.currentVisual.x * mapZoom,
                        top: dragState.currentVisual.y * mapZoom,
                        width: dragState.currentVisual.width * mapZoom,
                        height: dragState.currentVisual.height * mapZoom,
                        transform: `rotate(${dragState.currentVisual.rotation}deg)`,
                      }}
                    >
                      <strong>{dragState.tool === 'label' ? 'Label' : SPACE_STATUSES.find(([value]) => value === statusForMapTool(dragState.tool))?.[1]}</strong>
                    </div>
                  )}
                  {dragState?.mode === 'marquee' && (() => {
                    const rect = marqueeRect(
                      { x: dragState.startX, y: dragState.startY },
                      { x: dragState.currentX, y: dragState.currentY }
                    );
                    return (
                      <div
                        className="moorings-selection-marquee"
                        aria-hidden="true"
                        style={{
                          left: rect.left * mapZoom,
                          top: rect.top * mapZoom,
                          width: rect.width * mapZoom,
                          height: rect.height * mapZoom,
                        }}
                      />
                    );
                  })()}
                  {selectedAreaSpaces.map((space) => {
                    const previewVisual = dragState?.mode === 'group-move' ? dragState.previewVisuals?.[space.id] : null;
                    const primarySelected = selectedSpaceId === space.id;
                    const draftMatches = spaceDraft.id === space.id;
                    const visual = previewVisual || (draftMatches ? visualFromDraft(spaceDraft, activeAreaCanvas) : normalizeMooringVisual(space, activeAreaCanvas));
                    const selected = selectedSpaceIdSet.has(space.id) || primarySelected;
                    const renderedSpace = draftMatches ? { ...space, label: spaceDraft.label, status: spaceDraft.status } : space;
                    const isLabelMarker = visual.shape === 'label';
                    const allocation = allocations.find((entry) => entry.spaceId === space.id && String(entry.status || 'active') !== 'ended');
                    return (
                      <div
                        key={space.id}
                        role="button"
                        tabIndex={0}
                        className={`moorings-canvas-spot moorings-canvas-spot--${renderedSpace.status || 'vacant'} moorings-canvas-spot--${visual.shape}${selected ? ' is-selected' : ''}`}
                        style={{
                          left: visual.x * mapZoom,
                          top: visual.y * mapZoom,
                          width: visual.width * mapZoom,
                          height: visual.height * mapZoom,
                          transform: `rotate(${visual.rotation}deg)`,
                        }}
                        onPointerDown={(event) => handleSpotPointerDown(event, space)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            if (event.shiftKey || multiSelectMode) {
                              const nextIds = selectedSpaceIdSet.has(space.id)
                                ? selectedSpaceIds.filter((id) => id !== space.id)
                                : [...selectedSpaceIds, space.id];
                              setPrimarySelectedSpace(selectedSpaceIdSet.has(space.id) ? null : space, nextIds);
                            } else {
                              selectSpace(space);
                            }
                          }
                        }}
                        title={`${renderedSpace.label || 'Space'} · ${isLabelMarker ? 'Label marker' : (SPACE_STATUSES.find(([value]) => value === renderedSpace.status)?.[1] || renderedSpace.status)}`}
                      >
                        <strong>{renderedSpace.label || 'Space'}</strong>
                        {!isLabelMarker && <span>{allocation ? membersById.get(allocation.memberId) || allocation.memberNameSnapshot || 'Allocated' : SPACE_STATUSES.find(([value]) => value === renderedSpace.status)?.[1] || renderedSpace.status}</span>}
                        {primarySelected && selectedSpaceIds.length <= 1 && (
                          <>
                            {['nw', 'ne', 'sw', 'se'].map((edge) => (
                              <button
                                key={edge}
                                type="button"
                                aria-label={`Resize ${edge}`}
                                className={`moorings-resize-handle moorings-resize-handle--${edge}`}
                                onPointerDown={(event) => handleResizePointerDown(event, space, edge)}
                              />
                            ))}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="moorings-empty-builder">
                <strong>No mooring area yet</strong>
                <span>Open Areas to add the first map area.</span>
                <button type="button" className="btn btn--primary btn--sm" onClick={() => {
                  setAreaDraft(createAreaDraft());
                  setAreaPanelOpen(true);
                }}>Add area</button>
              </div>
            )}
          </section>

          <aside className="moorings-inspector">
            <section className="moorings-panel">
              <h3>{selectedSpaceIds.length > 1 ? `${selectedSpaceIds.length} spaces selected` : (selectedSpace ? 'Selected space' : 'Space inspector')}</h3>
              {selectedSpace ? (
                <form onSubmit={async (event) => {
                  event.preventDefault();
                  if (mapDraftDirtyRef.current) await flushMapDraft();
                }} onBlurCapture={() => {
                  if (mapDraftDirtyRef.current) flushMapDraft();
                }} className="modal-form">
                  <div className={`moorings-map-save-status moorings-map-save-status--${mapSaveStatus}`} role="status" aria-live="polite">
                    {mapSaveStatus === 'saving' ? 'Saving…' : mapSaveStatus === 'saved' ? 'Saved' : mapDraftDirty ? 'Unsaved changes' : 'Changes save automatically'}
                  </div>
                  <label>Label<input className="input" value={spaceDraft.label} onChange={(e) => setSpaceDraftField('label', e.target.value)} /></label>
                  <label>Marker type<select className="input" value={spaceDraft.shape === 'label' ? 'label' : 'space'} onChange={(e) => setMarkerType(e.target.value)}><option value="space">Mooring space</option><option value="label">Label marker</option></select></label>
                  {spaceDraft.shape !== 'label' && (
                    <>
                      <label>Status<select className="input" value={spaceDraft.status} onChange={(e) => setSpaceDraftField('status', e.target.value)}>{SPACE_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
                      <label>Shape<select className="input" value={spaceDraft.shape} onChange={(e) => setSpaceDraftField('shape', e.target.value)}><option value="rect">Block</option><option value="circle">Buoy</option></select></label>
                    </>
                  )}
                  <div className="moorings-quick-actions">
                    <button type="button" className="btn btn--secondary btn--sm" onClick={openNumberedCopies} disabled={busy}>
                      Create numbered copies
                    </button>
                    <button type="button" className="btn btn--ghost btn--sm" onClick={() => {
                      clearSpaceSelection();
                    }} disabled={busy}>
                      Clear {selectedSpaceIds.length > 1 ? 'all selections' : 'selection'}
                    </button>
                  </div>
                  <section className="moorings-inspector-section">
                    <h4>Payment</h4>
                    {selectedSpacePricePreview.length > 0 && (
                      <div className="moorings-payment-preview">
                        {selectedSpacePricePreview.map((price) => (
                          <span key={price.cadence}>{price.cadence}: {price.amount > 0 ? `${formatMoney(price.amount)} (${price.source})` : 'not priced'}</span>
                        ))}
                      </div>
                    )}
                    <details className="moorings-advanced-fields">
                      <summary>Custom space price</summary>
                      {renderPriceInputs(spaceDraft.mooringPrice, setSpacePriceField)}
                    </details>
                  </section>
                  <section className="moorings-inspector-section">
                    <h4>Restrictions</h4>
                    <div className="moorings-scale-helper">
                      <label>Scale: 1 grid square (m)<input className="input" type="number" min="0.1" step="0.1" value={drawingScaleMetres} onChange={(e) => setDrawingScaleMetres(e.target.value)} /></label>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={applyDrawingScaleToRestrictions}>Use drawing size</button>
                    </div>
                    {Number.isFinite(drawingScaleNumber) && drawingScaleNumber > 0 && spaceDraft.shape !== 'label' && (
                      <p className="moorings-scale-readout">
                        Drawn size: {draftMetres.length.toFixed(1)}m x {draftMetres.beam.toFixed(1)}m
                      </p>
                    )}
                    <div className="moorings-form-row moorings-form-row--restrictions">
                      <label>Max length (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={spaceDraft.maxLength} onChange={(e) => setScaledRestrictionField('maxLength', e.target.value)} /></label>
                      <label>Max beam (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={spaceDraft.maxBeam} onChange={(e) => setScaledRestrictionField('maxBeam', e.target.value)} /></label>
                      <label>Max draft (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={spaceDraft.maxDraft} onChange={(e) => setSpaceDraftField('maxDraft', e.target.value)} /></label>
                    </div>
                    <label>Allowed vessel types<input className="input" value={spaceDraft.vesselTypes} onChange={(e) => setSpaceDraftField('vesselTypes', e.target.value)} placeholder="Keelboat, dinghy" /></label>
                    <label>Club-owned boat
                      <select className="input" value={spaceDraft.clubBoatId || ''} onChange={(e) => {
                        const nextBoatId = e.target.value;
                        const nextBoat = sailingBoatsById.get(nextBoatId);
                        updateMapSpaceDraft((current) => ({
                          ...current,
                          clubBoatId: nextBoatId,
                          clubBoatLabel: nextBoatId ? safeSailingBoatLabel(nextBoat || {}) : '',
                          status: nextBoatId ? 'occupied' : current.status,
                          maxLength: nextBoat?.length ? String(nextBoat.length) : current.maxLength,
                          maxBeam: nextBoat?.beam ? String(nextBoat.beam) : current.maxBeam,
                          maxDraft: nextBoat?.draft ? String(nextBoat.draft) : current.maxDraft,
                        }));
                      }}>
                        <option value="">No club boat assigned</option>
                        {sortedSailingBoats.map((boat) => (
                          <option key={boat.id} value={boat.id}>{safeSailingBoatLabel(boat)}</option>
                        ))}
                      </select>
                    </label>
                  </section>
                  <div className="moorings-nudge-pad" aria-label="Nudge selected space">
                    <button type="button" className="moorings-nudge-pad__up" aria-label="Move up" onClick={() => nudgeSelectedSpace(0, -MOORING_CANVAS.snap)}>↑ Up</button>
                    <button type="button" className="moorings-nudge-pad__left" aria-label="Move left" onClick={() => nudgeSelectedSpace(-MOORING_CANVAS.snap, 0)}>← Left</button>
                    <button type="button" className="moorings-nudge-pad__right" aria-label="Move right" onClick={() => nudgeSelectedSpace(MOORING_CANVAS.snap, 0)}>Right →</button>
                    <button type="button" className="moorings-nudge-pad__down" aria-label="Move down" onClick={() => nudgeSelectedSpace(0, MOORING_CANVAS.snap)}>↓ Down</button>
                  </div>
                  <div className="moorings-adjust-controls" aria-label="Resize and rotate selected space">
                    <button type="button" onClick={() => resizeSelectedSpace(MOORING_CANVAS.snap, 0)}>Grow width</button>
                    <button type="button" onClick={() => resizeSelectedSpace(-MOORING_CANVAS.snap, 0)}>Shrink width</button>
                    <button type="button" onClick={() => resizeSelectedSpace(0, MOORING_CANVAS.snap)}>Grow height</button>
                    <button type="button" onClick={() => resizeSelectedSpace(0, -MOORING_CANVAS.snap)}>Shrink height</button>
                    <button type="button" onClick={() => rotateSelectedSpace(-15)}>↺ Rotate</button>
                    <button type="button" onClick={() => rotateSelectedSpace(15)}>Rotate ↻</button>
                  </div>
                  <label>Notes<textarea className="input" value={spaceDraft.notes} onChange={(e) => setSpaceDraftField('notes', e.target.value)} /></label>
                  <details className="moorings-advanced-fields">
                    <summary>Advanced position</summary>
                    <div className="moorings-form-row">
                      <label>X<input className="input" type="number" step={MOORING_CANVAS.snap} value={spaceDraft.x} onChange={(e) => setSpaceDraftField('x', e.target.value)} /></label>
                      <label>Y<input className="input" type="number" step={MOORING_CANVAS.snap} value={spaceDraft.y} onChange={(e) => setSpaceDraftField('y', e.target.value)} /></label>
                      <label>Rotate<input className="input" type="number" step="15" value={spaceDraft.rotation} onChange={(e) => setSpaceDraftField('rotation', e.target.value)} /></label>
                    </div>
                    <div className="moorings-form-row">
                      <label>Width<input className="input" type="number" min="24" step={MOORING_CANVAS.snap} value={spaceDraft.width} onChange={(e) => setSpaceDraftField('width', e.target.value)} /></label>
                      <label>Height<input className="input" type="number" min="24" step={MOORING_CANVAS.snap} value={spaceDraft.height} onChange={(e) => setSpaceDraftField('height', e.target.value)} /></label>
                    </div>
                  </details>
                  {spaceDraft.clubBoatId && <p className="text-muted">Club boat: {safeSailingBoatLabel(sailingBoatsById.get(spaceDraft.clubBoatId) || { name: spaceDraft.clubBoatLabel })}</p>}
                  {selectedAllocation && <p className="text-muted">Allocated to {membersById.get(selectedAllocation.memberId) || selectedAllocation.memberNameSnapshot || 'member'} · {selectedAllocation.vesselName || 'vessel not named'}</p>}
                  <div className="form-actions">
                    <SubmitButton type="submit" variant="primary" label="Save now" disabled={busy || mapSaveStatus === 'saving'} />
                    <SubmitButton type="button" variant="secondary" label="Delete" onClick={() => deleteRecord('mooringSpaces', selectedSpace.id, 'space', async () => {
                      setSelectedSpaceId('');
                      setSelectedSpaceIds([]);
                      replaceMapSpaceDraft(createSpaceDraft({ areaId: selectedAreaId }));
                    })} disabled={busy} />
                  </div>
                </form>
              ) : (
                <p className="text-muted">Select a space to edit it, or choose an add tool and click-drag on the map to draw.</p>
              )}
            </section>
          </aside>
        </div>
      )}

      {tab === 'spaces' && (
        <div className="moorings-editor__grid-layout">
          <section className="moorings-panel">
            <h3>{spaceDraft.id ? 'Edit space' : 'Add space'}</h3>
            <form onSubmit={saveSpace} className="modal-form">
              <label>Area<select className="input" value={spaceDraft.areaId || selectedAreaId} onChange={(e) => setDraftField(setSpaceDraft, 'areaId', e.target.value)}>{areas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}</select></label>
              <div className="moorings-form-row">
                <label>Label<input className="input" value={spaceDraft.label} onChange={(e) => setDraftField(setSpaceDraft, 'label', e.target.value)} /></label>
                <label>Row<input className="input" type="number" min="1" value={spaceDraft.row} onChange={(e) => setDraftField(setSpaceDraft, 'row', e.target.value)} /></label>
                <label>Column<input className="input" type="number" min="1" value={spaceDraft.column} onChange={(e) => setDraftField(setSpaceDraft, 'column', e.target.value)} /></label>
              </div>
              <label>Status<select className="input" value={spaceDraft.status} onChange={(e) => setDraftField(setSpaceDraft, 'status', e.target.value)}>{SPACE_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <div className="moorings-form-row">
                <label>Max length (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={spaceDraft.maxLength} onChange={(e) => setDraftField(setSpaceDraft, 'maxLength', e.target.value)} /></label>
                <label>Max beam (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={spaceDraft.maxBeam} onChange={(e) => setDraftField(setSpaceDraft, 'maxBeam', e.target.value)} /></label>
                <label>Max draft (m)<input className="input" type="number" inputMode="decimal" min="0" step="any" value={spaceDraft.maxDraft} onChange={(e) => setDraftField(setSpaceDraft, 'maxDraft', e.target.value)} /></label>
              </div>
              <label>Allowed vessel types<input className="input" value={spaceDraft.vesselTypes} onChange={(e) => setDraftField(setSpaceDraft, 'vesselTypes', e.target.value)} placeholder="Keelboat, dinghy" /></label>
              <label>Notes<textarea className="input" value={spaceDraft.notes} onChange={(e) => setDraftField(setSpaceDraft, 'notes', e.target.value)} /></label>
              <div className="form-actions">
                <SubmitButton type="submit" variant="primary" label={spaceDraft.id ? 'Save space' : 'Add space'} disabled={busy} />
                {spaceDraft.id && <SubmitButton type="button" variant="ghost" label="Cancel edit" onClick={() => setSpaceDraft(createSpaceDraft({ areaId: selectedAreaId }))} disabled={busy} />}
              </div>
            </form>
          </section>
          <section className="moorings-panel">
            <h3>Spaces</h3>
            <label className="moorings-search">
              <span>Search spaces</span>
              <input className="input" value={spaceSearch} onChange={(e) => setSpaceSearch(e.target.value)} placeholder="Label, area, or status" />
            </label>
            <div className="moorings-table">
              {filteredSpaces.map((space) => (
                <div key={space.id} className="moorings-table-row">
                  <label className="moorings-inline-label">
                    <span className="sr-only">Label for {space.label || 'space'}</span>
                    <input
                      className="input"
                      value={inlineLabelDrafts[space.id] ?? space.label ?? ''}
                      onChange={(event) => setInlineLabelDrafts((current) => ({ ...current, [space.id]: event.target.value }))}
                      onBlur={(event) => saveInlineLabel(space, event.currentTarget.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          event.currentTarget.value = space.label || '';
                          setInlineLabelDrafts((current) => ({ ...current, [space.id]: space.label || '' }));
                          event.currentTarget.blur();
                        }
                      }}
                      disabled={inlineLabelSavingId === space.id}
                    />
                    <small>{inlineLabelSavingId === space.id ? 'Saving…' : `${areasById.get(space.areaId)?.name || 'Area'} · row ${space.row}, col ${space.column}`}</small>
                    {inlineLabelErrors[space.id] && <small className="moorings-inline-error" role="alert">{inlineLabelErrors[space.id]}</small>}
                  </label>
                  <span>{SPACE_STATUSES.find(([value]) => value === space.status)?.[1] || space.status}</span>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => {
                    setTab('map');
                    setSelectedAreaId(space.areaId);
                    selectSpace(space);
                  }}>Map</button>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => setSpaceDraft(createSpaceDraft(space))}>Edit</button>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'allocations' && (
        <div className="moorings-editor__grid-layout">
          <section className="moorings-panel">
            <h3>{allocationDraft.id ? 'Edit allocation' : 'Add allocation'}</h3>
            <form onSubmit={saveAllocation} className="modal-form">
              <label>Payer type<select className="input" value={allocationDraft.payerType} onChange={(e) => setDraftField(setAllocationDraft, 'payerType', e.target.value)}><option value="member">Club member</option><option value="non_member">Non-member mooring customer</option></select></label>
              {allocationDraft.payerType === 'member' ? (
                <label>Member<select className="input" value={allocationDraft.memberId} onChange={(e) => setDraftField(setAllocationDraft, 'memberId', e.target.value)}><option value="">Choose member</option>{memberProfiles.map((member) => <option key={member.id} value={member.id}>{displayNameFromProfile(member)}</option>)}</select></label>
              ) : (
                <label>Customer name<input className="input" value={allocationDraft.memberNameSnapshot} onChange={(e) => setDraftField(setAllocationDraft, 'memberNameSnapshot', e.target.value)} placeholder="Name shown to admins" /></label>
              )}
              {allocationDraft.payerType === 'non_member' ? (
                <label>Payer email<input className="input" type="email" value={allocationDraft.payerEmail} onChange={(e) => setDraftField(setAllocationDraft, 'payerEmail', e.target.value)} placeholder="name@example.com" /></label>
              ) : (
                <p className="moorings-form-note">Payment links use the selected member's account email.</p>
              )}
              <label>Space<select className="input" value={allocationDraft.spaceId} onChange={(e) => setDraftField(setAllocationDraft, 'spaceId', e.target.value)}><option value="">Choose space</option>{sortedSpaces.map((space) => <option key={space.id} value={space.id}>{areasById.get(space.areaId)?.name || 'Area'} · {space.label}</option>)}</select></label>
              <div className="moorings-form-row">
                <label>Status<select className="input" value={allocationDraft.status} onChange={(e) => setDraftField(setAllocationDraft, 'status', e.target.value)}><option value="active">Active</option><option value="reserved">Reserved</option><option value="ended">Ended</option></select></label>
                <label>Start<input className="input" type="date" value={allocationDraft.startDate} onChange={(e) => setDraftField(setAllocationDraft, 'startDate', e.target.value)} /></label>
                <label>End<input className="input" type="date" value={allocationDraft.endDate} onChange={(e) => setDraftField(setAllocationDraft, 'endDate', e.target.value)} /></label>
              </div>
              <div className="moorings-form-row">
                <label>Vessel<input className="input" value={allocationDraft.vesselName} onChange={(e) => setDraftField(setAllocationDraft, 'vesselName', e.target.value)} /></label>
                <label>Type<input className="input" value={allocationDraft.vesselType} onChange={(e) => setDraftField(setAllocationDraft, 'vesselType', e.target.value)} /></label>
              </div>
              <label>Boat class
                <select className="input" value={allocationDraft.boatClassId} onChange={(e) => {
                  const boatClass = boatClassesById.get(e.target.value);
                  setAllocationDraft((current) => ({
                    ...current,
                    boatClassId: e.target.value,
                    boatClassNameSnapshot: boatClass?.name || '',
                  }));
                }}>
                  <option value="">Choose boat class</option>
                  {allocationDraft.boatClassId && !boatClassesById.has(allocationDraft.boatClassId) && (
                    <option value={allocationDraft.boatClassId}>{allocationDraft.boatClassNameSnapshot || 'Removed boat class'} (no longer configured)</option>
                  )}
                  {boatClasses.map((boatClass) => <option key={boatClass.id} value={boatClass.id}>{boatClass.name}</option>)}
                </select>
              </label>
              <div className="moorings-form-row">
                <label>Length<input className="input" type="number" inputMode="decimal" min="0" step="any" value={allocationDraft.length} onChange={(e) => setDraftField(setAllocationDraft, 'length', e.target.value)} /></label>
                <label>Beam<input className="input" type="number" inputMode="decimal" min="0" step="any" value={allocationDraft.beam} onChange={(e) => setDraftField(setAllocationDraft, 'beam', e.target.value)} /></label>
                <label>Draft<input className="input" type="number" inputMode="decimal" min="0" step="any" value={allocationDraft.draft} onChange={(e) => setDraftField(setAllocationDraft, 'draft', e.target.value)} /></label>
              </div>
              <section className="moorings-inspector-section">
                <h4>Payment</h4>
                {allocationPricePreview.length > 0 && (
                  <div className="moorings-payment-preview">
                    {allocationPricePreview.map((price) => (
                      <span key={price.cadence}>{price.cadence}: {price.amount > 0 ? `${formatMoney(price.amount)} (${price.source})` : 'not priced'}</span>
                    ))}
                  </div>
                )}
                {paymentSettings.baselineCalculation !== 'space' ? (
                  <details className="moorings-advanced-fields">
                    <summary>Custom allocation price</summary>
                    <p className="moorings-advanced-help">Use only when this allocation needs a one-off price different from the selected club pricing model.</p>
                    {renderPriceInputs(allocationDraft.customPrices, setAllocationPriceField)}
                  </details>
                ) : (
                  <p className="moorings-form-note">This allocation uses the selected space price. Change the price on the Map or Spaces tab.</p>
                )}
                {allocationDraft.id && (
                  <div className="moorings-payment-link-actions">
                    <button type="button" className="btn btn--secondary btn--sm" onClick={() => createPaymentLinkForAllocation()} disabled={busy}>
                      {allocationDraft.paymentLinkId ? 'Regenerate payment link' : 'Create payment link'}
                    </button>
                    {allocationDraft.paymentLinkId && <button type="button" className="btn btn--ghost btn--sm" onClick={copyAllocationPaymentLink} disabled={busy}>Copy link</button>}
                  </div>
                )}
                {!allocationDraft.id && <p className="text-muted">Save the allocation before creating an email-locked payment link.</p>}
              </section>
              <label>Payment override<select className="input" value={allocationDraft.paymentOverrideStatus} onChange={(e) => setDraftField(setAllocationDraft, 'paymentOverrideStatus', e.target.value)}><option value="">Use bolt-on status</option><option value="paid">Paid</option><option value="unpaid">Unpaid</option><option value="overdue">Overdue</option><option value="pending">Pending</option><option value="waived">Waived</option></select></label>
              <label>Private notes<textarea className="input" value={allocationDraft.privateNotes} onChange={(e) => setDraftField(setAllocationDraft, 'privateNotes', e.target.value)} /></label>
              <div className="form-actions">
                <SubmitButton type="submit" variant="primary" label={allocationDraft.id ? 'Save allocation' : 'Add allocation'} disabled={busy} />
                {allocationDraft.id && <SubmitButton type="button" variant="ghost" label="Cancel edit" onClick={() => setAllocationDraft(createAllocationDraft())} disabled={busy} />}
              </div>
            </form>
          </section>
          <section className="moorings-panel">
            <h3>Allocations</h3>
            <div className="moorings-table">
              {allocations.map((allocation) => (
                <div key={allocation.id} className="moorings-table-row">
                  <span><strong>{membersById.get(allocation.memberId) || allocation.memberNameSnapshot || 'Member'}</strong><small>{spacesById.get(allocation.spaceId)?.label || 'Space'} · {allocation.vesselName || 'Vessel not named'}{(boatClassesById.get(allocation.boatClassId)?.name || allocation.boatClassNameSnapshot) ? ` · ${boatClassesById.get(allocation.boatClassId)?.name || allocation.boatClassNameSnapshot}` : ''}</small></span>
                  <span>{paymentStatusByAllocationId[allocation.id] || 'not linked'}</span>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => setAllocationDraft(createAllocationDraft(allocation))}>Edit</button>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'payments' && (
        <div className="moorings-editor__grid-layout">
          <section className="moorings-panel">
            <h3>Mooring payment settings</h3>
            <form onSubmit={savePaymentSettings} className="modal-form">
              <p className="text-muted">Currency: {currency}. Mooring fees are generated as hidden bolt-ons and paid through mooring payment links.</p>
              <div className="moorings-form-row">
                <label>Baseline calculation<select className="input" value={paymentDraft.baselineCalculation} onChange={(e) => setPaymentField('baselineCalculation', e.target.value)}><option value="flat">Club flat rate</option><option value="area">By area</option><option value="length">By vessel length</option><option value="space">By individual space</option><option value="class">By boat class</option></select></label>
                <label>Payment cadence<select className="input" value={paymentDraft.cadenceMode} onChange={(e) => setPaymentField('cadenceMode', e.target.value)}><option value="annual">Annual</option><option value="monthly">Monthly</option><option value="payer_choice">Payer chooses monthly or annual</option></select></label>
                <label>Stripe fee handling<select className="input" value={paymentDraft.stripeFeeHandling} onChange={(e) => setPaymentField('stripeFeeHandling', e.target.value)}><option value="deduct">Deduct Stripe fees from club payout</option><option value="add">Add Stripe fees at checkout</option></select></label>
              </div>
              {paymentDraft.cadenceMode === 'payer_choice' && (
                <section className="moorings-inspector-section">
                  <h4>Monthly and annual pricing</h4>
                  <label>Price entry
                    <select className="input" value={payerChoicePriceEntryMode} onChange={(e) => setPaymentField('payerChoicePriceEntryMode', e.target.value)}>
                      <option value="annual">Enter annual prices, derive monthly</option>
                      <option value="monthly">Enter monthly prices, derive annual</option>
                      <option value="separate">Enter monthly and annual separately</option>
                    </select>
                  </label>
                  <p className="moorings-form-note">
                    This applies to flat, area, length, space, boat class, and allocation custom prices so payer-choice checkout always has both cadences.
                  </p>
                </section>
              )}
              {paymentDraft.baselineCalculation === 'flat' && (
                <section className="moorings-inspector-section">
                  <h4>Club flat rate</h4>
                  {renderPriceInputs(paymentDraft.flatPrices, (field, value) => setPaymentPriceField('flatPrices', field, value))}
                </section>
              )}
              {paymentDraft.baselineCalculation === 'length' && (
                <section className="moorings-inspector-section">
                  <h4>Length rate per metre</h4>
                  {renderRateInputs(paymentDraft.lengthRates, (field, value) => setPaymentPriceField('lengthRates', field, value))}
                </section>
              )}
              {paymentDraft.baselineCalculation === 'area' && (
                <section className="moorings-inspector-section">
                  <h4>Area prices</h4>
                  <label>Area pricing type<select className="input" value={paymentDraft.areaPricingMode} onChange={(e) => setPaymentField('areaPricingMode', e.target.value)}><option value="fixed">Fixed price per area</option><option value="sqm">Rate per square metre</option></select></label>
                  {paymentDraft.areaPricingMode === 'sqm' && (
                    <p className="moorings-form-note">Square metre pricing uses allocation length x beam, or the selected space's max length x max beam if the allocation has no vessel dimensions yet.</p>
                  )}
                  {areas.length === 0 ? (
                    <p className="text-muted">Add an area before setting area-specific rates.</p>
                  ) : (
                    <div className="moorings-list">
                      {areas.map((area) => {
                        const areaDraft = paymentDraft.areaPricingMode === 'sqm'
                          ? (paymentDraft.areaRates?.[area.id] || createSquareRateDraft())
                          : (paymentDraft.areaPrices?.[area.id] || createPriceDraft());
                        return (
                          <div key={area.id} className="moorings-area-price-card">
                            <strong>{area.name}</strong>
                            {paymentDraft.areaPricingMode === 'sqm'
                              ? renderSquareRateInputs(areaDraft, (field, value) => setPaymentPriceField('areaRates', field, value, area.id))
                              : renderPriceInputs(areaDraft, (field, value) => setPaymentPriceField('areaPrices', field, value, area.id))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              )}
              {paymentDraft.baselineCalculation === 'space' && (
                <section className="moorings-inspector-section">
                  <h4>Space prices</h4>
                  <p className="text-muted">Set the member and non-member price on each space from the Map tab. Allocations use the selected space price.</p>
                </section>
              )}
              {paymentDraft.baselineCalculation === 'class' && (
                <section className="moorings-inspector-section">
                  <div className="moorings-panel-heading">
                    <div>
                      <h4>Boat classes</h4>
                      <p className="text-muted">Create club-defined pricing bands, then assign one to each allocation.</p>
                    </div>
                    <button type="button" className="btn btn--secondary btn--sm" onClick={addBoatClass}>Add class</button>
                  </div>
                  {(paymentDraft.boatClasses || []).length === 0 ? (
                    <p className="text-muted">Add at least one class to use this pricing model.</p>
                  ) : (
                    <div className="moorings-list">
                      {(paymentDraft.boatClasses || []).map((boatClass) => (
                        <div key={boatClass.id} className="moorings-area-price-card">
                          <div className="moorings-panel-heading">
                            <label>Class name<input className="input" value={boatClass.name} onChange={(e) => setBoatClassField(boatClass.id, 'name', e.target.value)} placeholder="Class A" /></label>
                            <button type="button" className="btn btn--ghost btn--sm" onClick={() => removeBoatClass(boatClass.id)}>Delete</button>
                          </div>
                          {renderPriceInputs(boatClass.prices || createPriceDraft(), (field, value) => setBoatClassPriceField(boatClass.id, field, value))}
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              )}
              <div className="form-actions">
                <SubmitButton type="submit" variant="primary" label="Save payment settings" disabled={busy} />
              </div>
            </form>
          </section>
          <section className="moorings-panel moorings-pricing-guide">
            <h3>How this price is used</h3>
            {paymentDraft.baselineCalculation === 'space' ? (
              <p className="text-muted">Each allocation is priced from its mooring space. Edit a space on the Map tab to set its price.</p>
            ) : paymentDraft.baselineCalculation === 'class' ? (
              <p className="text-muted">Each allocation needs a valid boat class. Existing allocations remain unpriced until an admin assigns one.</p>
            ) : (
              <p className="text-muted">Allocations use the selected club pricing model. A one-off allocation price is still available from the allocation editor for exceptions.</p>
            )}
          </section>
        </div>
      )}

      {tab === 'waitlist' && (
        <div className="moorings-waitlist-manager">
          <section className="moorings-panel moorings-waitlist-map-panel">
            <div className="moorings-panel-heading">
              <h3>Waitlist map</h3>
              <p>Click a space to see people who requested it first, followed by suitable general requests.</p>
            </div>
            <div className="moorings-map-topbar">
              <div className="moorings-area-tabs" aria-label="Waitlist map areas">
                <button type="button" className={!waitlistSelectedSpaceId ? 'active' : ''} onClick={() => {
                  setWaitlistSelectedSpaceId('');
                  setSelectedWaitlistEntryId('');
                }}>
                  General waitlist
                  {generalWaitlistEntries.length > 0 ? ` (${generalWaitlistEntries.length})` : ''}
                </button>
                {areas.map((area) => (
                  <button key={area.id} type="button" className={area.id === selectedArea?.id ? 'active' : ''} onClick={() => {
                    setSelectedAreaId(area.id);
                    setWaitlistSelectedSpaceId('');
                  }}>
                    {area.name}
                  </button>
                ))}
              </div>
              <div className="moorings-map-zoom">
                <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMapZoom((value) => Math.max(0.6, Number((value - 0.1).toFixed(1))))}>-</button>
                <span>{Math.round(mapZoom * 100)}%</span>
                <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMapZoom((value) => Math.min(1.6, Number((value + 0.1).toFixed(1))))}>+</button>
              </div>
            </div>
            {selectedArea ? (
              <div className="moorings-map-scroll moorings-map-scroll--waitlist">
                <div
                  className="moorings-canvas moorings-canvas--waitlist"
                  style={{
                    width: activeAreaCanvas.width * mapZoom,
                    height: activeAreaCanvas.height * mapZoom,
                    '--moorings-zoom': mapZoom,
                  }}
                >
                  <div className="moorings-canvas-boundary" aria-hidden="true" />
                  <div className="moorings-canvas__area-name">{selectedArea.name}</div>
                  {selectedAreaSpaces.map((space) => {
                    const visual = normalizeMooringVisual(space, activeAreaCanvas);
                    const selected = waitlistSelectedSpaceId === space.id;
                    const requestCount = waitlistCountsBySpaceId.get(space.id) || 0;
                    const isLabelMarker = visual.shape === 'label';
                    return (
                      <button
                        key={space.id}
                        type="button"
                        className={`moorings-canvas-spot moorings-canvas-spot--${space.status || 'vacant'} moorings-canvas-spot--${visual.shape}${selected ? ' is-selected' : ''}${requestCount ? ' has-waitlist-requests' : ''}`}
                        style={{
                          left: visual.x * mapZoom,
                          top: visual.y * mapZoom,
                          width: visual.width * mapZoom,
                          height: visual.height * mapZoom,
                          transform: `rotate(${visual.rotation}deg)`,
                        }}
                        onClick={() => setWaitlistSelectedSpaceId(space.id)}
                      >
                        <strong>{space.label || 'Space'}</strong>
                        {!isLabelMarker && <span>{SPACE_STATUSES.find(([value]) => value === space.status)?.[1] || space.status}</span>}
                        {requestCount > 0 && <em>{requestCount}</em>}
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="moorings-empty-builder">
                <strong>No mooring area yet</strong>
                <span>Add map areas before managing space-specific requests.</span>
              </div>
            )}
            <details className="moorings-advanced-fields" open={manualWaitlistOpen} onToggle={(event) => setManualWaitlistOpen(event.currentTarget.open)}>
              <summary>Manual waitlist entry</summary>
              <form onSubmit={saveWaitlist} className="modal-form moorings-manual-waitlist-form">
                <label>Member<select className="input" value={waitlistDraft.memberId} onChange={(e) => setDraftField(setWaitlistDraft, 'memberId', e.target.value)}><option value="">Choose member</option>{memberProfiles.map((member) => <option key={member.id} value={member.id}>{displayNameFromProfile(member)}</option>)}</select></label>
                <div className="moorings-form-row">
                  <label>Status<select className="input" value={waitlistDraft.status} onChange={(e) => setDraftField(setWaitlistDraft, 'status', e.target.value)}><option value="waiting">Waiting</option><option value="offered">Offered</option><option value="allocated">Allocated</option><option value="withdrawn">Withdrawn</option></select></label>
                  <label>Priority<input className="input" type="number" min="0" value={waitlistDraft.priority} onChange={(e) => setDraftField(setWaitlistDraft, 'priority', e.target.value)} /></label>
                  <label>Applied<input className="input" type="date" value={waitlistDraft.appliedAt} onChange={(e) => setDraftField(setWaitlistDraft, 'appliedAt', e.target.value)} /></label>
                </div>
                <div className="moorings-form-row">
                  <label>Vessel<input className="input" value={waitlistDraft.vesselName} onChange={(e) => setDraftField(setWaitlistDraft, 'vesselName', e.target.value)} /></label>
                  <label>Type<input className="input" value={waitlistDraft.vesselType} onChange={(e) => setDraftField(setWaitlistDraft, 'vesselType', e.target.value)} /></label>
                </div>
                {boatClasses.length > 0 && (
                  <label>Boat class<select className="input" value={waitlistDraft.boatClassId} onChange={(e) => setDraftField(setWaitlistDraft, 'boatClassId', e.target.value)}><option value="">Choose boat class</option>{boatClasses.map((boatClass) => <option key={boatClass.id} value={boatClass.id}>{boatClass.name}</option>)}</select></label>
                )}
                <div className="moorings-form-row">
                  <label>Length<input className="input" type="number" inputMode="decimal" min="0" step="any" value={waitlistDraft.length} onChange={(e) => setDraftField(setWaitlistDraft, 'length', e.target.value)} /></label>
                  <label>Beam<input className="input" type="number" inputMode="decimal" min="0" step="any" value={waitlistDraft.beam} onChange={(e) => setDraftField(setWaitlistDraft, 'beam', e.target.value)} /></label>
                  <label>Draft<input className="input" type="number" inputMode="decimal" min="0" step="any" value={waitlistDraft.draft} onChange={(e) => setDraftField(setWaitlistDraft, 'draft', e.target.value)} /></label>
                </div>
                <label>Admin notes<textarea className="input" value={waitlistDraft.adminNotes} onChange={(e) => setDraftField(setWaitlistDraft, 'adminNotes', e.target.value)} /></label>
                {selectedWaitlistSuitability.length > 0 && (
                  <div className="moorings-suggestions">
                    <strong>Suitable vacant spaces</strong>
                    {selectedWaitlistSuitability.map((space) => <span key={space.id}>{areasById.get(space.areaId)?.name || 'Area'} · {space.label}</span>)}
                  </div>
                )}
                <div className="form-actions">
                  <SubmitButton type="submit" variant="primary" label={waitlistDraft.id ? 'Save waitlist entry' : 'Add to waitlist'} disabled={busy} />
                  {waitlistDraft.id && <SubmitButton type="button" variant="ghost" label="Cancel edit" onClick={() => {
                    setWaitlistDraft(createWaitlistDraft());
                    setSelectedWaitlistEntryId('');
                  }} disabled={busy} />}
                </div>
              </form>
            </details>
          </section>
          <aside className="moorings-panel moorings-waitlist-queue">
            <div className="moorings-panel-heading">
              <h3>{waitlistSelectedSpace ? waitlistSelectedSpace.label : 'General waitlist'}</h3>
              <p>{waitlistSelectedSpace ? `${areasById.get(waitlistSelectedSpace.areaId)?.name || 'Area'} · ${visualWaitlistQueue.length} open request${visualWaitlistQueue.length === 1 ? '' : 's'}` : `${visualWaitlistQueue.length} general request${visualWaitlistQueue.length === 1 ? '' : 's'}`}</p>
            </div>
            {waitlistSelectedSpace && (
              <div className="moorings-waitlist-space-summary">
                <span>{SPACE_STATUSES.find(([value]) => value === waitlistSelectedSpace.status)?.[1] || waitlistSelectedSpace.status}</span>
                <div className="moorings-waitlist-space-summary__actions">
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => setWaitlistSelectedSpaceId('')}>General waitlist</button>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => {
                    setTab('map');
                    setSelectedAreaId(waitlistSelectedSpace.areaId);
                    selectSpace(waitlistSelectedSpace);
                  }}>Open on map</button>
                </div>
              </div>
            )}
            <div className="moorings-waitlist-list">
              {visualWaitlistQueue.length === 0 ? (
                <p className="text-muted">{waitlistSelectedSpace ? 'No open requests match this space yet.' : 'No general waitlist requests yet. Click a space to manage space-specific requests.'}</p>
              ) : visualWaitlistQueue.map((entry) => (
                <button key={entry.id} type="button" className={`moorings-waitlist-card${selectedWaitlistEntryId === entry.id ? ' is-selected' : ''}`} onClick={() => openWaitlistEntryDetail(entry, waitlistSelectedSpace)}>
                  <span className={`moorings-waitlist-card__match moorings-waitlist-card__match--${entry.matchType || 'general'}`}>
                    {entry.matchType === 'requested' ? 'Requested this space' : entry.matchType === 'suitable' ? 'Suitable general request' : entry.matchType === 'other_space' ? 'Requested elsewhere' : 'General request'}
                  </span>
                  <strong>{membersById.get(entry.memberId) || entry.memberNameSnapshot || 'Member'}</strong>
                  <small>{mooringWaitlistTargetLabel(entry)} · {entry.vesselName || 'Vessel not named'}</small>
                  <small>{[entry.vesselType, entry.length ? `${entry.length}m` : '', `Priority ${entry.priority ?? 'none'}`, formatShortDate(entry.appliedAt) || 'unknown date'].filter(Boolean).join(' · ')}</small>
                </button>
              ))}
            </div>
          </aside>
          {selectedWaitlistEntry && (
            <div className="moorings-waitlist-detail-backdrop" role="presentation" onClick={() => setSelectedWaitlistEntryId('')}>
              <section className="moorings-waitlist-detail" role="dialog" aria-modal="true" aria-label="Waitlist request" onClick={(event) => event.stopPropagation()}>
                <div className="moorings-waitlist-detail__header">
                  <div>
                    <span className="moorings-waitlist-card__match">{mooringWaitlistTargetLabel(selectedWaitlistEntry)}</span>
                    <h3>{membersById.get(selectedWaitlistEntry.memberId) || selectedWaitlistEntry.memberNameSnapshot || 'Member'}</h3>
                    <p>{selectedWaitlistEntry.vesselName || 'Vessel not named'}{selectedWaitlistEntry.vesselType ? ` · ${selectedWaitlistEntry.vesselType}` : ''}</p>
                  </div>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => setSelectedWaitlistEntryId('')}>Close</button>
                </div>
                <dl className="moorings-waitlist-detail__facts">
                  <div><dt>Status</dt><dd>{selectedWaitlistEntry.status || 'waiting'}</dd></div>
                  <div><dt>Applied</dt><dd>{formatShortDate(selectedWaitlistEntry.appliedAt) || 'Unknown'}</dd></div>
                  <div><dt>Priority</dt><dd>{selectedWaitlistEntry.priority ?? 'None'}</dd></div>
                  <div><dt>Dimensions</dt><dd>{selectedWaitlistDimensions || 'Not recorded'}</dd></div>
                </dl>
                <div className="moorings-form-row moorings-form-row--compact">
                  <label>Status<select className="input" value={waitlistDraft.status} onChange={(e) => setDraftField(setWaitlistDraft, 'status', e.target.value)}><option value="waiting">Waiting</option><option value="offered">Offered</option><option value="allocated">Allocated</option><option value="withdrawn">Withdrawn</option></select></label>
                  <label>Priority<input className="input" type="number" min="0" value={waitlistDraft.priority} onChange={(e) => setDraftField(setWaitlistDraft, 'priority', e.target.value)} /></label>
                  <label>Applied<input className="input" type="date" value={waitlistDraft.appliedAt} onChange={(e) => setDraftField(setWaitlistDraft, 'appliedAt', e.target.value)} /></label>
                </div>
                {boatClasses.length > 0 && (
                  <label>Confirmed boat class<select className="input" value={waitlistDraft.boatClassId} onChange={(e) => setDraftField(setWaitlistDraft, 'boatClassId', e.target.value)}><option value="">Choose boat class</option>{boatClasses.map((boatClass) => <option key={boatClass.id} value={boatClass.id}>{boatClass.name}</option>)}</select></label>
                )}
                {selectedWaitlistEntry.memberNote && (
                  <div className="moorings-waitlist-detail__note">
                    <strong>Member note</strong>
                    <p>{selectedWaitlistEntry.memberNote}</p>
                  </div>
                )}
                {waitlistActionSpace && (
                  <div className="moorings-waitlist-detail__note">
                    <strong>Selected space</strong>
                    <p>{areasById.get(waitlistActionSpace.areaId)?.name || 'Area'} · {waitlistActionSpace.label}</p>
                  </div>
                )}
                <label>Admin note<textarea className="input" value={waitlistDraft.adminNotes} onChange={(e) => setDraftField(setWaitlistDraft, 'adminNotes', e.target.value)} /></label>
                <label>Decision note<textarea className="input" value={waitlistDraft.decisionNote} onChange={(e) => setDraftField(setWaitlistDraft, 'decisionNote', e.target.value)} placeholder="Optional note stored with offer/withdrawal" /></label>
                <div className="moorings-waitlist-detail__actions">
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => offerWaitlistSpace()} disabled={busy || !waitlistActionSpace}>
                    Offer this space
                  </button>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => allocateWaitlistEntry()} disabled={busy || !waitlistActionSpace}>
                    Allocate now
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={saveWaitlist} disabled={busy}>
                    Save notes/status
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => withdrawWaitlistEntry()} disabled={busy}>
                    Withdraw
                  </button>
                </div>
              </section>
            </div>
          )}
        </div>
      )}

      {tab === 'maintenance' && (
        <div className="moorings-editor__grid-layout">
          <section className="moorings-panel">
            <h3>{maintenanceDraft.id ? 'Edit maintenance' : 'Add maintenance'}</h3>
            <form onSubmit={saveMaintenance} className="modal-form">
              <label>Title<input className="input" value={maintenanceDraft.title} onChange={(e) => setDraftField(setMaintenanceDraft, 'title', e.target.value)} /></label>
              <label>Space<select className="input" value={maintenanceDraft.spaceId} onChange={(e) => setDraftField(setMaintenanceDraft, 'spaceId', e.target.value)}><option value="">General / no space</option>{sortedSpaces.map((space) => <option key={space.id} value={space.id}>{areasById.get(space.areaId)?.name || 'Area'} · {space.label}</option>)}</select></label>
              <div className="moorings-form-row">
                <label>Type<select className="input" value={maintenanceDraft.type} onChange={(e) => setDraftField(setMaintenanceDraft, 'type', e.target.value)}><option value="inspection">Inspection</option><option value="repair">Repair</option><option value="chain_replacement">Chain replacement</option><option value="dredging">Dredging</option><option value="other">Other</option></select></label>
                <label>Status<select className="input" value={maintenanceDraft.status} onChange={(e) => setDraftField(setMaintenanceDraft, 'status', e.target.value)}><option value="open">Open</option><option value="scheduled">Scheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option></select></label>
              </div>
              <div className="moorings-form-row">
                <label>Due<input className="input" type="date" value={maintenanceDraft.dueDate} onChange={(e) => setDraftField(setMaintenanceDraft, 'dueDate', e.target.value)} /></label>
                <label>Completed<input className="input" type="date" value={maintenanceDraft.completedAt} onChange={(e) => setDraftField(setMaintenanceDraft, 'completedAt', e.target.value)} /></label>
              </div>
              <label>Notes<textarea className="input" value={maintenanceDraft.notes} onChange={(e) => setDraftField(setMaintenanceDraft, 'notes', e.target.value)} /></label>
              <div className="form-actions">
                <SubmitButton type="submit" variant="primary" label={maintenanceDraft.id ? 'Save maintenance' : 'Add maintenance'} disabled={busy} />
                {maintenanceDraft.id && <SubmitButton type="button" variant="secondary" label="Delete" onClick={() => deleteRecord('mooringMaintenance', maintenanceDraft.id, 'maintenance entry', () => setMaintenanceDraft(createMaintenanceDraft()))} disabled={busy} />}
              </div>
            </form>
          </section>
          <section className="moorings-panel">
            <h3>Maintenance records</h3>
            <div className="moorings-table">
              {maintenance.map((entry) => (
                <div key={entry.id} className="moorings-table-row">
                  <span><strong>{entry.title}</strong><small>{spacesById.get(entry.spaceId)?.label || 'General'} · {formatShortDate(entry.dueDate) || 'No due date'}</small></span>
                  <span>{entry.status || 'open'}</span>
                  <button type="button" className="btn btn--ghost btn--xs" onClick={() => setMaintenanceDraft(createMaintenanceDraft(entry))}>Edit</button>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'settings' && (
        <div className="moorings-settings-layout">
          <section className="moorings-panel moorings-form-card">
            <div className="moorings-panel-heading">
              <h3>Member visibility</h3>
              <p>Choose what signed-in club members can see and request from the moorings card.</p>
            </div>
            <form onSubmit={saveSettings} className="modal-form moorings-settings-form">
              <label>Club member view<select className="input" value={memberVisibility} onChange={(e) => setMemberVisibility(e.target.value)}>{VISIBILITY_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
              <div className="moorings-toggle-list">
                <label className="moorings-checkbox"><input type="checkbox" checked={showMemberMap} onChange={(e) => setShowMemberMap(e.target.checked)} /> <span>Show the mooring map to members</span></label>
                <label className="moorings-checkbox"><input type="checkbox" checked={allowMemberWaitlistRequests} onChange={(e) => setAllowMemberWaitlistRequests(e.target.checked)} /> <span>Let members join the mooring waitlist</span></label>
              </div>
              <p className="moorings-form-note">Visibility is always limited to signed-in club members. Admin notes, payment references, and raw user IDs are not shown in member views.</p>
              <div className="form-actions">
                <SubmitButton type="submit" variant="primary" label="Save settings" disabled={busy} />
              </div>
            </form>
          </section>
        </div>
      )}
      {stripeGuardDialog}
    </div>
  );
}
